/**
 * Onboarding utility functions
 *
 * This module provides utility functions for the onboarding flow: question navigation,
 * data formatting, and financial calculations.
 *
 * **V2 agent chat:** The message list this code treats as `chatData` is the same array
 * stored under **`v2_onboarding_ai.chat`** in API responses (`GET /api/v2/onboarding-ai`
 * and related).
 */
import config, { logger } from '../config';
import {
  parseNumericValue,
  calculateAgeFromBirthYear,
  extractAssetsAndDebts,
  isMarried,
  ASSET_SOURCE_FIELDS, DEBT_SOURCE_FIELDS,
  AI_RESPONSE_INSTRUCTIONS,
  FINANCIAL_CONSTANTS,
  HEALTHCARE_COSTS,
  getSocialSecurityMonthlyBenefit,
  getMissingRequiredDashboardFields
} from './helper';
import { getToken, getApiHeaders } from './api';

/** Longevity success description when score is 0 or below 40% */
const RETIREMENT_SUCCESS_ROOM_TO_GROW =
  "Based on your current score, there's room to grow! 🌱 We're here to help you strengthen your plan and steadily reach the level of freedom & confidence that CFPs benchmark for success.";

/** Shown when longevity_success_score is missing or invalid (not when score is 0) */
const RETIREMENT_SUCCESS_UNABLE_TO_CALCULATE =
  'Unable to calculate Longevity Success. Please try again later.';

export function normalizeLongevityPercent(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object') {
    const nested = value.percentage ?? value.score ?? value.value ?? value.longevity_success_score;
    if (nested === value) return null;
    return normalizeLongevityPercent(nested);
  }
  const n = Number(String(value).replace('%', '').trim());
  if (!Number.isFinite(n)) return null;
  const pct = n > 0 && n < 1 ? n * 100 : n;
  return Math.max(0, Math.min(100, Math.round(pct)));
}

function readLongevitySuccessScore(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const candidates = [
    payload.longevity_success_score,
    payload.longevitySuccessScore,
    payload.success_score,
    payload.successRate,
    payload.retirement_success_score,
    payload.data?.longevity_success_score,
    payload.result?.longevity_success_score,
    payload.metrics?.longevity_success_score,
    payload.simulation?.longevity_success_score,
    payload.output?.longevity_success_score,
  ];
  for (const candidate of candidates) {
    if (candidate == null || candidate === '') continue;
    const parsed = normalizeLongevityPercent(candidate);
    if (parsed != null) return parsed;
  }
  return null;
}

/**
 * Normalizes legacy onboardingProgress keys that included a redundant category prefix.
 *
 * Current format (backend): "children_number"
 *
 * This returns a shallow copy with the normalized keys added. Existing current-format
 * keys take precedence over derived legacy-normalized keys.
 */
export const normalizeOnboardingProgressKeys = (onboardingProgress) => {
  if (!onboardingProgress || typeof onboardingProgress !== 'object' || Array.isArray(onboardingProgress)) {
    return {};
  }

  const normalized = { ...onboardingProgress };

  for (const [key, value] of Object.entries(onboardingProgress)) {
    const match = String(key).match(/^\d+-(\d+_.+)$/);
    if (!match) continue;
    const newKey = match[1];
    if (normalized[newKey] === undefined) {
      normalized[newKey] = value;
    }
  }

  return normalized;
};

/** Panel + optional scenario strings for `v2_onboarding_ai_result` (PUT /api/v2/onboarding-ai/update-result). */
export const calculatedFinancialDataToV2OnboardingAiResult = (calculatedData) => {
  const fp = normalizeOnboardingProgressKeys(calculatedData?.onboardingProgress || {});
  return {
    financial_profile: fp,
    updated_at: Date.now(),
    duration: calculatedData?.duration,
    spouseAgeText: calculatedData?.spouseAgeText,
    healthcareCostsText: calculatedData?.healthcareCostsText,
    socialSecurityText: calculatedData?.socialSecurityText,
    longevityText: calculatedData?.longevityText,
    financialIndependenceText: calculatedData?.financialIndependenceText
  };
};

/** Progress object from stored v2 result (or legacy nested `onboardingProgress`). */
export const onboardingProgressFromV2OnboardingAiResult = (v2Result) => {
  if (!v2Result || typeof v2Result !== 'object' || Array.isArray(v2Result)) return null;
  const fp = v2Result.financial_profile;
  if (fp != null) {
    if (typeof fp === 'object' && !Array.isArray(fp)) {
      return fp;
    }
    if (typeof fp === 'string') {
      try {
        const parsed = JSON.parse(fp);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return parsed;
        }
      } catch (_) {
        /* ignore */
      }
    }
  }
  if (v2Result.onboardingProgress && typeof v2Result.onboardingProgress === 'object' && !Array.isArray(v2Result.onboardingProgress)) {
    return v2Result.onboardingProgress;
  }
  return null;
};

/**
 * Ensure `v2_onboarding_ai_result.financial_profile` is a plain object (parses JSON string).
 */
export function normalizeV2OnboardingAiResult(v2) {
  if (!v2 || typeof v2 !== 'object' || Array.isArray(v2)) return null;
  const fp = v2.financial_profile;
  if (fp == null) {
    return { ...v2 };
  }
  if (typeof fp === 'string') {
    try {
      const parsed = JSON.parse(fp);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return { ...v2, financial_profile: parsed };
      }
    } catch (_) {
      /* ignore */
    }
    return { ...v2, financial_profile: {} };
  }
  if (typeof fp === 'object' && !Array.isArray(fp)) {
    return { ...v2, financial_profile: { ...fp } };
  }
  return { ...v2, financial_profile: {} };
}

/**
 * Resolves onboarding progress for an admin user from cached v2 result or AI chat conversion.
 * Falls back to `fallbackOnboarding` when cache and chat are unavailable or incomplete.
 *
 * @param {{
 *   result: { chatData?: array, onboardingAiResult?: object|null }|null,
 *   fallbackOnboarding?: object,
 *   useAdminEndpoint?: boolean
 * }} params
 * @returns {Promise<{ progress: object, financialDataResult: object|null, fromCache: boolean }>}
 */
export async function resolveOnboardingProgressFromAdminData({
  result,
  fallbackOnboarding = {},
  useAdminEndpoint = true,
}) {
  const fallback = normalizeOnboardingProgressKeys(fallbackOnboarding || {});

  if (!result) {
    return { progress: fallback, financialDataResult: null, fromCache: false };
  }

  const cachedData = result.onboardingAiResult;
  if (cachedData && typeof cachedData === 'object' && !Array.isArray(cachedData)) {
    const v2Norm = normalizeV2OnboardingAiResult(cachedData) ?? cachedData;
    const cachedProgress =
      onboardingProgressFromV2OnboardingAiResult(v2Norm) ?? cachedData.onboardingProgress ?? null;
    const normalizedCached = normalizeOnboardingProgressKeys(cachedProgress || {});
    if (getMissingRequiredDashboardFields(normalizedCached).length === 0) {
      return { progress: normalizedCached, financialDataResult: null, fromCache: true };
    }
  }

  const chatData = result?.chatData || [];
  if (chatData.length === 0) {
    return { progress: fallback, financialDataResult: null, fromCache: false };
  }

  const financialDataResult = await calculateAllFinancialDataAI(chatData, { useAdminEndpoint });
  const convertedProgress = normalizeOnboardingProgressKeys(financialDataResult?.onboardingProgress || {});
  if (Object.keys(convertedProgress).length > 0) {
    return { progress: convertedProgress, financialDataResult, fromCache: false };
  }

  return { progress: fallback, financialDataResult: null, fromCache: false };
}

// Helper function to format numbers with commas
export const formatNumberWithCommas = (value) => {
  if (!value || value === "") return "";

  // Remove existing commas and non-digit characters except decimal
  const cleanValue = value.toString().replace(/[^\d.]/g, '');

  if (cleanValue === "" || isNaN(parseFloat(cleanValue))) return "";

  // Trim leading zeros (except for decimal numbers like 0.5)
  let trimmedValue = cleanValue;
  if (cleanValue.length > 1 && cleanValue.startsWith('0') && !cleanValue.startsWith('0.')) {
    trimmedValue = cleanValue.replace(/^0+/, '');
    // If all zeros were removed, keep at least one zero
    if (trimmedValue === '') {
      trimmedValue = '0';
    }
  }

  // Split by decimal point to handle decimals
  const parts = trimmedValue.split('.');
  // Format the whole number part with commas
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  // Rejoin with decimal if it exists
  return parts.join('.');
};

// ============================================================================
// FINANCIAL CALCULATION FUNCTIONS
// ============================================================================

/**
 * Financial calculation and scenario utilities for retirement planning
 *
 * This section provides comprehensive financial planning calculations including:
 * - College education fund cost planning for children
 * - Healthcare cost estimates
 * - Social Security benefit calculations
 * - Net worth calculations from assets and debts
 *
 * All calculations use compound interest, inflation adjustments, and time-value-of-money principles
 * to provide realistic retirement planning scenarios.
 */

/**
 * Gets duration value for display based on onboarding progress
 * Enhanced to parse duration from longer text like "I have 2 years..."
 * @param {Object} onboardingProgress - Onboarding progress data
 * @returns {string} Formatted duration string
 */
export const getDurationValue = (onboardingProgress) => {
  const durationAnswer = onboardingProgress["duration"];
  if (!durationAnswer) return "1 year";

  // First check if it's a predefined duration
  const durationMap = {
    "1-3 months": "3 months",
    "3-6 months": "6 months",
    "1 year": "1 year",
    "2 years": "2 years"
  };

  if (durationMap[durationAnswer]) {
    return durationMap[durationAnswer];
  }

  // If not predefined, try to parse duration from longer text
  const parsedDuration = parseDurationFromText(durationAnswer);
  if (parsedDuration) {
    return parsedDuration;
  }

  // Fallback to original answer if parsing fails
  return durationAnswer;
};

/**
 * Parses duration from longer text like "I have 2 years..." or "My career shift will take 3 years"
 * @param {string} text - Text that may contain duration information
 * @returns {string|null} Parsed duration string or null if not found
 */
const parseDurationFromText = (text) => {
  if (!text || typeof text !== 'string') return null;

  const cleanText = text.toLowerCase().trim();
  const durationPatterns = [
    /(\d+(?:\.\d+)?)\s*years?/, /(\d+(?:\.\d+)?)\s*year/, /(\d+(?:\.\d+)?)-year/,
    /(\d+(?:\.\d+)?)\s*yr/, /(\d+(?:\.\d+)?)y/,
    /(\d+(?:\.\d+)?)\s*months?/, /(\d+(?:\.\d+)?)\s*month/,
    /(\d+(?:\.\d+)?)\s*mo/, /(\d+(?:\.\d+)?)m/
  ];

  for (const pattern of durationPatterns) {
    const match = cleanText.match(pattern);
    if (match) {
      const number = parseFloat(match[1]);
      if (number > 0) {
        const isMonths = pattern.source.includes('month') || pattern.source.includes('mo') || pattern.source.includes('m');
        if (isMonths) {
          if (number >= 12) {
            const years = Math.round(number / 12);
            return `${years} year${years > 1 ? 's' : ''}`;
          }
          return `${number} month${number > 1 ? 's' : ''}`;
        } else {
          return number % 1 === 0 ? `${number} year${number > 1 ? 's' : ''}` : `${number} years`;
        }
      }
    }
  }

  return null;
};

/**
 * Calculates spouse's age and returns appropriate text based on family situation
 * @param {Object} onboardingProgress - Onboarding progress data
 * @returns {string} Formatted spouse age text
 */
/**
 * Gets spouse/partner age text based on family situation
 *
 * Returns spouse age if user is married and partner YOB is available.
 * Returns "not specified" if user is single or partner YOB is missing.
 *
 * @param {Object} onboardingProgress - Onboarding progress data
 * @returns {string} Formatted spouse age text or "not specified" message
 */
export const getSpouseAgeText = (onboardingProgress) => {
  // Validate input
  if (!onboardingProgress || typeof onboardingProgress !== 'object') {
    return "While your spouse's/partner's age is not specified";
  }

  const familySituation = onboardingProgress["family_situation"];

  // Check if user is single - if so, return immediately without spouse info
  if (!isMarried(familySituation)) {
    return "While your spouse's/partner's age is not specified";
  }

  // User is married, check for partner YOB
  const partnerYob = onboardingProgress["partner_year_of_birth_of_birth"];
  if (!partnerYob) {
    return "While your spouse's/partner's age is not specified";
  }

  // Calculate and return spouse age
  const spouseAge = calculateAgeFromBirthYear(partnerYob);
  if (!spouseAge || isNaN(spouseAge)) {
    return "While your spouse's/partner's age is not specified";
  }

  return `While your spouse's/partner's age is ${spouseAge}`;
};

/**
 * Gets healthcare costs text based on family situation for retirement planning
 *
 * Healthcare costs are one of the largest expenses in retirement, often underestimated.
 * This function provides personalized healthcare cost estimates based on family structure:
 * - Single individuals: $197,500 lifetime healthcare costs
 * - Married couples: $395,000 lifetime healthcare costs
 *
 * These estimates are based on Fidelity's annual retiree health care cost studies
 * and represent out-of-pocket costs for Medicare premiums, deductibles, copays,
 * and other healthcare expenses not covered by insurance.
 *
 * @param {Object} onboardingProgress - Onboarding progress data
 * @returns {string} Formatted healthcare costs text with family-specific messaging
 */
export const getHealthcareCostsText = (onboardingProgress) => {
  // Validate input
  if (!onboardingProgress || typeof onboardingProgress !== 'object') {
    return `Your healthcare costs approximately will be $${HEALTHCARE_COSTS.SINGLE.toLocaleString('en-US')} lifetime for you.`;
  }

  const familySituation = onboardingProgress["family_situation"];
  const married = isMarried(familySituation);

  const cost = married ? HEALTHCARE_COSTS.MARRIED : HEALTHCARE_COSTS.SINGLE;
  const recipient = married ? "you and your spouse/partner" : "you";

  return `Your healthcare costs approximately will be $${cost.toLocaleString('en-US')} lifetime for ${recipient}.`;
};

/**
 * Gets Social Security benefits text based on family situation and user-specified ages
 *
 * Social Security is a critical component of retirement income planning. This function
 * creates personalized messaging about Social Security benefits based on:
 * - User's planned Social Security claiming age
 * - Spouse's planned Social Security claiming age (if married)
 * - Conservative benefit assumptions (80% of expected benefits)
 *
 * IMPORTANT CONSIDERATIONS:
 * - Social Security benefits can be claimed as early as age 62 (reduced benefits)
 * - Full retirement age (FRA) varies by birth year (currently 65 for most people)
 * - Delaying benefits past FRA increases monthly benefits up to age 70
 * - The 80% assumption accounts for potential future benefit reductions
 *
 * @param {Object} onboardingProgress - Onboarding progress data
 * @returns {string} Formatted Social Security benefits text with family-specific information
 */
export const getSocialSecurityText = (onboardingProgress) => {
  // Validate input
  if (!onboardingProgress || typeof onboardingProgress !== 'object') {
    return `Social Security benefits are assumed to start for you at age ${FINANCIAL_CONSTANTS.SOCIAL_SECURITY_BENEFIT_AGE}. For your spouse/partner this is not specified.<br />A cautionary assumption of ${FINANCIAL_CONSTANTS.BENEFITS_PERCENTAGE}% of benefits is applied.`;
  }

  const familySituation = onboardingProgress["family_situation"];
  const rawUserSS = parseInt(onboardingProgress["your_social_security_benefits_age"], 10);
  const userSocialSecurityAge = (!isNaN(rawUserSS) && rawUserSS >= 62 && rawUserSS <= 100)
    ? rawUserSS
    : FINANCIAL_CONSTANTS.SOCIAL_SECURITY_BENEFIT_AGE;

  // Check if user is single - if so, don't show spouse info
  let spouseText = "";
  if (isMarried(familySituation)) {
    const rawSpouseSS = parseInt(onboardingProgress["partner_social_security_benefits_age"], 10);
    const spouseSocialSecurityAge = (!isNaN(rawSpouseSS) && rawSpouseSS >= 62 && rawSpouseSS <= 100)
      ? rawSpouseSS
      : FINANCIAL_CONSTANTS.SOCIAL_SECURITY_BENEFIT_AGE;
    spouseText = ` For your spouse/partner start at age ${spouseSocialSecurityAge}.`;
  } else {
    spouseText = " For your spouse/partner this is not specified.";
  }

  return `Social Security benefits are assumed to start for you at age ${userSocialSecurityAge}.${spouseText}<br />A cautionary assumption of ${FINANCIAL_CONSTANTS.BENEFITS_PERCENTAGE}% of benefits is applied.`;
};

/**
 * Gets financial independence text with retirement ages
 *
 * This function creates personalized messaging about financial independence goals
 *
 * RETIREMENT PLANNING CONCEPTS:
 * - Financial Independence: Having sufficient assets to cover expenses indefinitely
 * - Retirement Age: The age at which you plan to stop working
 * - Dual Retirement Planning: Married couples may have different retirement ages
 *
 * @param {Object} onboardingProgress - Onboarding progress data
 * @returns {string} Formatted financial independence text with retirement ages
 */
export const getFinancialIndependenceText = (onboardingProgress) => {
  // Validate input
  if (!onboardingProgress || typeof onboardingProgress !== 'object') {
    return `The goal is to achieve financial independence and retire. Your spouse/partner retirement age is not specified.`;
  }

  const familySituation = onboardingProgress["family_situation"];
  const userRetirementAge = parseInt(onboardingProgress["retirement_age"]);

  // Check if user is single - if so, don't show spouse info
  let spouseRetirementText = "";
  if (isMarried(familySituation)) {
    const spouseRetirementAge = parseInt(onboardingProgress["partner_retire"]);
    if (spouseRetirementAge && spouseRetirementAge > 0) {
      spouseRetirementText = `Your spouse/partner plans to retire at age ${spouseRetirementAge}.`;
    } else {
      spouseRetirementText = "Your spouse/partner retirement age is not specified.";
    }
  } else {
    spouseRetirementText = "Your spouse/partner retirement age is not specified.";
  }

  return `The goal is to achieve financial independence and retire at age ${userRetirementAge}. ${spouseRetirementText}`;
};

/**
 * Gets longevity text based on family situation for retirement planning
 *
 * Life expectancy is a critical assumption in retirement planning because it determines
 * how long retirement savings need to last. This function creates personalized messaging
 * about longevity assumptions used in financial planning calculations.
 *
 * LONGEVITY CONSIDERATIONS:
 * - Default assumption: FINANCIAL_CONSTANTS.LIFE_EXPECTANCY years (conservative estimate)
 * - Family history and health factors can influence actual life expectancy
 * - Longer life expectancy requires larger retirement savings
 * - Planning for longer life ensures you don't outlive your savings
 *
 * RETIREMENT PLANNING IMPACT:
 * - Longer life expectancy = longer retirement period = higher savings needs
 * - Conservative assumptions help ensure adequate funding
 * - Family situation affects longevity planning (spousal considerations)
 *
 * @param {Object} onboardingProgress - Onboarding progress data
 * @returns {string} Formatted longevity text with family-specific messaging
 */
export const getLongevityText = (onboardingProgress) => {
  const LIFE_EXPECTANCY = FINANCIAL_CONSTANTS.LIFE_EXPECTANCY;

  // Validate input
  if (!onboardingProgress || typeof onboardingProgress !== 'object') {
    return `For planning purposes, this scenario assumes you live up to ${LIFE_EXPECTANCY} years.`;
  }

  const familySituation = onboardingProgress["family_situation"];

  // Check if user is single - if so, don't mention spouse
  if (isMarried(familySituation)) {
    return `For planning purposes, this scenario assumes you and your spouse/partner live up to ${LIFE_EXPECTANCY} years.`;
  } else {
    return `For planning purposes, this scenario assumes you live up to ${LIFE_EXPECTANCY} years.`;
  }
};

// ============================================================================
// EXPENSE CALCULATION UTILITIES
// ============================================================================

// Scenario thresholds as percentages of total income
const SCENARIO_THRESHOLDS = {
  SCENARIO1: 0.5,  // 50% or more of total income
  SCENARIO2: 0.25, // 25% to 49% of total income
  SCENARIO3: 0.0,  // 1% to 24% of total income (positive but less than 25%)
  SCENARIO4: -Infinity // Deficit (0 and negative result)
};

// Default return value for invalid inputs
const DEFAULT_RESULT = {
  result: 0,
  scenario: 'scenario4',
  monthlyIncomeAfterCareerShift: 0,
  monthlyExpenses: 0
};

/**
 * Determines the financial scenario based on the result and total income
 *
 * @param {number} result - Monthly savings/deficit (income - expenses)
 * @param {number} totalIncome - Total monthly income
 * @returns {string} Scenario identifier ('scenario1' through 'scenario4')
 */
const determineScenario = (result, totalIncome) => {
  // If no income or deficit, default to scenario4
  if (totalIncome <= 0 || result <= 0) {
    return 'scenario4';
  }

  // Calculate result as percentage of total income
  const resultPercentage = result / totalIncome;

  // Determine scenario based on thresholds (checking from highest to lowest)
  if (resultPercentage >= SCENARIO_THRESHOLDS.SCENARIO1) {
    return 'scenario1';
  }
  if (resultPercentage >= SCENARIO_THRESHOLDS.SCENARIO2) {
    return 'scenario2';
  }
  if (resultPercentage > SCENARIO_THRESHOLDS.SCENARIO3) {
    return 'scenario3';
  }

  return 'scenario4';
};

/**
 * Calculates expense result and determines financial scenario
 * Uses question IDs as keys (e.g., "monthly_net_income_after_career_shift", "monthly_net_income_partner")
 *
 * Formula: Monthly Savings/Deficit = (Individual estimated monthly net income after career shift once stabilized + Partner net monthly income if married) - Monthly Expenses
 *
 * Scenarios:
 * - Scenario 1: Savings >= 50% of total income (excellent financial position)
 * - Scenario 2: Savings >= 25% and < 50% of total income (strong financial position)
 * - Scenario 3: Savings > 0% and < 25% of total income (positive but tight)
 * - Scenario 4: Deficit (expenses >= income) or no income
 *
 * @param {Object} onboardingProgress - Object containing financial data with keys like "monthly_net_income_after_career_shift", "monthly_net_income_partner", and "monthly_expenses"
 * @returns {Object} Object containing:
 *   - result: {number} Monthly savings/deficit amount
 *   - scenario: {string} Scenario identifier ('scenario1' through 'scenario4')
 *   - monthlyIncomeAfterCareerShift: {number} Individual estimated monthly net income after career shift once stabilized (user only)
 *   - monthlyExpenses: {number} Monthly expenses
 */
const calculateExpenseResult = (onboardingProgress) => {
  // Validate input
  if (!onboardingProgress || typeof onboardingProgress !== 'object') {
    console.warn('Invalid onboardingProgress provided to calculateExpenseResult');
    return DEFAULT_RESULT;
  }

  // Extract and parse financial data
  // parseNumericValue handles various formats: "100k", "My salary is 10k", "1,500", "$2000", "$5K", etc.
  const netMonthlyIncomeAfterCareerShift = parseNumericValue(
    onboardingProgress["monthly_net_income_after_career_shift"],
    0
  );
  // `monthly_net_income_partner` only applies when `family_situation` is Married/Partnered; `isMarried()` matches that.
  const familySituation = onboardingProgress["family_situation"];
  const monthlyNetIncomePartner = isMarried(familySituation)
    ? parseNumericValue(onboardingProgress["monthly_net_income_partner"], 0)
    : 0;
  const totalMonthlyIncomeAfterCareerShift = netMonthlyIncomeAfterCareerShift + monthlyNetIncomePartner;
  const monthlyExpenses = parseNumericValue(
    onboardingProgress["monthly_expenses"],
    0
  );

  // Calculate monthly savings/deficit: Income - Expenses
  const result = totalMonthlyIncomeAfterCareerShift - monthlyExpenses;

  // Determine financial scenario based on result (use total income for scenario threshold)
  const scenario = determineScenario(result, totalMonthlyIncomeAfterCareerShift);

  return {
    result,
    scenario,
    monthlyIncomeAfterCareerShift: netMonthlyIncomeAfterCareerShift,
    monthlyExpenses
  };
};

// ============================================================================
// FINANCIAL WELLNESS UTILITIES
// ============================================================================

/**
 * Generates trend data for net worth chart
 * @param {number} baseValue - Starting value
 * @param {number} monthlyChange - Monthly change amount
 * @param {number} months - Number of months to generate
 * @returns {Array} Trend data array
 */
const generateTrendData = (baseValue, monthlyChange, months = 9) => {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'];
  return Array.from({ length: months }, (_, i) => ({
    month: monthNames[i],
    value: Math.round(baseValue + (monthlyChange * (i + 1) / months))
  }));
};

/**
 * Parses duration value from onboarding progress and converts it to months
 * Handles various formats including:
 * - Predefined options: "1-3 months", "3-6 months", "1 year", "2 years"
 * - Custom text: "I have 2 years...", "My career shift will take 3 years", "12 months"
 * - Numeric values: 12, 24, 1.5 (assumed to be months if < 24, years if >= 24)
 * - Range formats: "1-3 months" (takes maximum), "6-12 months" (takes maximum)
 * - Decimal values: "1.5 years", "18.5 months"
 * - Various abbreviations: "1y", "12mo", "2yr", "24m"
 * - Custom duration field: "duration_custom"
 *
 * @param {Object} onboardingProgress - Onboarding progress data
 * @returns {number} Duration in months (0 if invalid or not found)
 */
export const parseDurationToMonths = (onboardingProgress) => {
  try {
    if (!onboardingProgress || typeof onboardingProgress !== 'object') return 0;
    // Try multiple possible field name formats (priority order): standard keys, then AI-style
    let durationAnswer = onboardingProgress["duration_custom"] ||
      onboardingProgress["duration"] ||
      onboardingProgress["duration"] ||
      onboardingProgress["career_shift_duration"];

    // If value is already a number (e.g. months from AI), use when in reasonable range (1–120 months)
    const asNumber = typeof durationAnswer === 'number' ? durationAnswer : parseNumericValue(durationAnswer, null);
    if (asNumber != null && !Number.isNaN(asNumber) && asNumber >= 1 && asNumber <= 120) {
      return Math.round(asNumber);
    }

    if (!durationAnswer) {
      logger.warn('⚠️  Duration not found in onboarding progress, defaulting to 0 months');
      return 0;
    }

    // Handle null, undefined, empty string, or whitespace-only
    if (durationAnswer == null ||
      (typeof durationAnswer === 'string' && durationAnswer.trim() === '')) {
      logger.warn('⚠️  Duration is empty or null, defaulting to 0 months');
      return 0;
    }

    // Handle boolean false (edge case)
    if (durationAnswer === false) {
      logger.warn('⚠️  Duration is false, defaulting to 0 months');
      return 0;
    }

    // Convert to string for pattern matching
    const durationString = String(durationAnswer).toLowerCase().trim();

    // CRITICAL: Check string patterns FIRST before numeric heuristic
    // This ensures "5 years" is correctly parsed as 60 months, not 5 months

    // Expanded predefined duration map (case-insensitive matching)
    const durationMap = {
      "1-3 months": 3,
      "3-6 months": 6,
      "6-12 months": 12,
      "1 year": 12,
      "2 years": 24,
      "3 years": 36,
      "4 years": 48,
      "5 years": 60,
      "1-3 month": 3,
      "3-6 month": 6,
      "6-12 month": 12,
      "1 yr": 12,
      "2 yrs": 24,
      "1y": 12,
      "2y": 24,
      "12mo": 12,
      "24mo": 24
    };

    // Check exact matches first (case-insensitive)
    const normalizedMapKey = durationString;
    if (durationMap[normalizedMapKey]) {
      const months = durationMap[normalizedMapKey];
      return months;
    }

    // Handle range formats (e.g., "1-3 months", "6-12 months")
    // Extract the maximum value from the range
    const rangePattern = /(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)\s*(months?|month|mo|m|years?|year|yr|y)/i;
    const rangeMatch = durationString.match(rangePattern);
    if (rangeMatch) {
      const minValue = parseFloat(rangeMatch[1]);
      const maxValue = parseFloat(rangeMatch[2]);
      const unit = rangeMatch[3].toLowerCase();

      if (!isNaN(minValue) && !isNaN(maxValue) && minValue > 0 && maxValue > 0) {
        // Use the maximum value from the range
        const isMonths = unit.includes('month') || unit.includes('mo') || (unit === 'm' && maxValue < 24);
        const months = isMonths
          ? Math.round(maxValue)
          : Math.round(maxValue * 12);
        return months;
      }
    }

    // Enhanced pattern matching with more variations
    // Order matters: more specific patterns first
    const durationPatterns = [
      // Years patterns (more specific first)
      /(\d+(?:\.\d+)?)\s*years?/i,
      /(\d+(?:\.\d+)?)\s*year/i,
      /(\d+(?:\.\d+)?)-year/i,
      /(\d+(?:\.\d+)?)\s*yrs?/i,
      /(\d+(?:\.\d+)?)\s*yr/i,
      /(\d+(?:\.\d+)?)y(?!\w)/i, // 'y' not followed by word character
      // Months patterns (more specific first)
      /(\d+(?:\.\d+)?)\s*months?/i,
      /(\d+(?:\.\d+)?)\s*month/i,
      /(\d+(?:\.\d+)?)\s*mos?/i,
      /(\d+(?:\.\d+)?)\s*mo/i,
      /(\d+(?:\.\d+)?)m(?!\w)/i, // 'm' not followed by word character (to avoid matching words like "am", "pm")
      // Weeks (convert to months)
      /(\d+(?:\.\d+)?)\s*weeks?/i,
      /(\d+(?:\.\d+)?)\s*week/i,
      /(\d+(?:\.\d+)?)\s*wks?/i,
      /(\d+(?:\.\d+)?)\s*wk/i,
      /(\d+(?:\.\d+)?)w(?!\w)/i,
      // Days (convert to months, though unlikely)
      /(\d+(?:\.\d+)?)\s*days?/i,
      /(\d+(?:\.\d+)?)\s*day/i,
      /(\d+(?:\.\d+)?)d(?!\w)/i
    ];

    for (const pattern of durationPatterns) {
      const match = durationString.match(pattern);
      if (match) {
        const number = parseFloat(match[1]);
        if (!isNaN(number) && number > 0) {
          const patternSource = pattern.source.toLowerCase();
          let months;

          // Determine unit type
          if (patternSource.includes('year') || patternSource.includes('yr') || patternSource.includes('y')) {
            // Years: convert to months
            months = Math.round(number * 12);
          } else if (patternSource.includes('month') || patternSource.includes('mo') || patternSource.includes('m')) {
            // Months: use directly
            months = Math.round(number);
          } else if (patternSource.includes('week') || patternSource.includes('wk') || patternSource.includes('w')) {
            // Weeks: convert to months (approximate: 4.33 weeks per month)
            months = Math.round(number / 4.33);
          } else if (patternSource.includes('day') || patternSource.includes('d')) {
            // Days: convert to months (approximate: 30.44 days per month)
            months = Math.round(number / 30.44);
          } else {
            // Fallback: assume months for unknown patterns
            months = Math.round(number);
          }

          return months;
        }
      }
    }

    // Try to extract any number from the string as a last resort
    const anyNumberMatch = durationString.match(/(\d+(?:\.\d+)?)/);
    if (anyNumberMatch) {
      const number = parseFloat(anyNumberMatch[1]);
      if (!isNaN(number) && number > 0) {
        // Heuristic: if number is reasonable (< 120), assume months; otherwise might be days/weeks
        let months;
        if (number < 120) {
          months = Math.round(number);
        } else {
          // Large number, might be days - convert to months
          months = Math.round(number / 30.44);
        }
        logger.warn(`   ⚠️  Could not parse unit, extracted number: ${number} → ${months} months (heuristic)`);
        return months;
      }
    }

    // LAST RESORT: Handle pure numeric values (no text, just numbers)
    // Only use this if the string is a pure number with no text indicators
    const numericValue = parseNumericValue(durationAnswer);
    if (numericValue !== null && numericValue !== undefined && numericValue >= 0) {
      // Check if the original string is a pure number (no letters)
      const isPureNumber = /^\d+(?:\.\d+)?$/.test(String(durationAnswer).trim());
      if (isPureNumber) {
        // Heuristic: if number < 24, assume months; if >= 24, assume it could be months or years
        // But if it's a round number divisible by 12 and >= 12, it's likely years
        let months;
        if (numericValue < 24) {
          // Likely months
          months = Math.round(numericValue);
        } else if (numericValue >= 24 && numericValue % 12 === 0) {
          // Likely years (e.g., 24, 36, 48) - but this is ambiguous, so keep as months
          // Actually, if it's divisible by 12, it could be years, so multiply by 12
          months = Math.round(numericValue);
        } else {
          // Could be either, but default to months for larger numbers
          months = Math.round(numericValue);
        }
        logger.warn(`   ⚠️  Pure number detected (no unit): ${numericValue} → ${months} months (heuristic)`);
        return months;
      }
    }

    // If all parsing fails, return 0
    logger.warn(`   ❌ Could not parse duration: "${durationAnswer}", defaulting to 0 months`);
    return 0;
  } catch (error) {
    logger.error('❌ Error parsing duration to months:', error);
    return 0;
  }
};

/**
 * Determines the base income for a given year based on duration period
 * @param {number} yearFromStart - Years from start of projection
 * @param {number} durationMonths - Duration period in months
 * @param {number} annualizedMonthlyNetIncomeDuringCareerShift - Annualized individual estimated monthly net income during career shift period (monthly × 12)
 * @param {number} annualIncomeAfterCareerShift - Annual net income after career shift (monthly × 12)
 * @returns {Object} Object with baseAnnualIncome and incomeSource
 */
const getBaseIncomeForYear = (yearFromStart, durationMonths, annualizedMonthlyNetIncomeDuringCareerShift, annualIncomeAfterCareerShift) => {
  if (durationMonths === 0) {
    return {
      baseAnnualIncome: annualIncomeAfterCareerShift,
      incomeSource: 'Annual income after career shift (duration = 0)'
    };
  }

  const monthsFromStart = yearFromStart * 12;
  const isWithinDurationPeriod = monthsFromStart < durationMonths;

  if (isWithinDurationPeriod) {
    return {
      baseAnnualIncome: annualizedMonthlyNetIncomeDuringCareerShift,
      incomeSource: `Individual estimated monthly net income during career shift (annualized) - within ${durationMonths} month duration period`
    };
  }

  return {
    baseAnnualIncome: annualIncomeAfterCareerShift,
    incomeSource: 'Annual income after career shift - after duration period'
  };
};

/**
 * Formats currency value for display
 * @param {number} value - Numeric value to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Formats currency value in compact form with 2 decimal places for millions
 * Removes .00 from the end if present (e.g., 62.00M becomes 62M)
 * @param {number} value - Numeric value to format
 * @returns {string} Formatted compact currency string with 2 decimal places
 */
export const formatCompactCurrencyTwoDecimals = (value) => {
  if (value >= 1000000) {
    const formatted = `$${(value / 1000000).toFixed(2)}M`;
    // Remove .00 from the end if present
    return formatted.replace(/\.00M$/, 'M');
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return formatCurrency(value);
};

/**
 * Parses children college entries from various formats to a per-child array.
 *
 * New V2 meaning:
 * - `children_college_years` is a list with one slot per child
 * - each slot may be free text (e.g. "unknown", "not going"), a 4-digit year string, null, or ""
 *
 * Back-compat:
 * - accepts legacy formats like "Child 1: 2022\nChild 2: 2030" or "[2022, 2030]"
 *
 * @param {string|Array} collegeData - College data in various formats
 * @returns {Array<string|null>} Per-child entries (strings, "" allowed, or null)
 */
const parseChildrenCollegeData = (collegeData) => {
  // Handle null or undefined
  if (collegeData === null || collegeData === undefined) {
    return [];
  }

  // If already an array, normalize to string|null entries
  if (Array.isArray(collegeData)) {
    return collegeData.map((item) => {
      if (item === undefined) return null;
      if (item === null) return null;
      // Legacy object format: { year, amountPaid }
      if (typeof item === 'object') {
        if (item && 'year' in item && item.year != null) return String(item.year);
        return String(item);
      }
      return String(item);
    });
  }

  // If not a string, return empty array
  if (typeof collegeData !== 'string' || !collegeData.trim()) {
    return [];
  }

  const raw = collegeData.trim();

  // JSON array string
  if (raw.startsWith('[')) {
    try {
      const j = JSON.parse(raw);
      if (Array.isArray(j)) return parseChildrenCollegeData(j);
    } catch (_) {
      /* ignore */
    }
  }

  const result = [];
  // Split by newlines, but also handle cases where data might be on single line
  let lines = raw.split('\n').filter(line => line.trim());

  // If no newlines found but contains "Child" keyword, try to split by "Child" pattern
  if (lines.length === 1 && raw.match(/Child\s+\d+/i)) {
    // Split by "Child" keyword while keeping the "Child" part
    const childMatches = raw.matchAll(/(Child\s+\d+\s*:\s*[^\n]+)/gi);
    const matches = Array.from(childMatches);
    if (matches.length > 0) {
      lines = matches.map(m => m[1].trim());
    }
  }

  lines.forEach((line) => {
    if (!line || typeof line !== 'string') return;

    // Prefer 4-digit year when present; otherwise keep free text.
    const yearMatch = line.match(/\b(20\d{2})\b/);
    if (yearMatch) {
      result.push(yearMatch[1]);
      return;
    }
    // Strip leading "Child N:" prefix if present.
    const cleaned = line.replace(/^\s*Child\s+\d+\s*[:-]\s*/i, '').trim();
    result.push(cleaned);
  });

  return result;
};

function extractNumericCollegeYears(collegeEntries) {
  const arr = parseChildrenCollegeData(collegeEntries);
  const out = [];
  for (const entry of arr) {
    if (entry == null) continue;
    const m = String(entry).match(/\b(20\d{2})\b/);
    if (!m) continue;
    const y = parseInt(m[1], 10);
    if (!Number.isNaN(y) && y >= 2000 && y <= 2100) out.push(y);
  }
  return out;
}

/**
 * Extracts a 4-digit birth year (1900-2100) from text. Used for your_year_of_birth and partner_year_of_birth_of_birth (year-only).
 *
 * @param {string} text - Raw answer text
 * @returns {string|null} Year as "YYYY" or null
 */
const parseYearFromText = (text) => {
  if (!text || typeof text !== 'string') return null;
  const m = String(text).trim().match(/\b(19[0-9]{2}|20[0-9]{2}|2100)\b/);
  if (!m) return null;
  const y = parseInt(m[1], 10);
  return (y >= 1900 && y <= 2100) ? m[1] : null;
};

/**
 * Normalizes a YOB value to birth year (YYYY) for your_year_of_birth / partner_year_of_birth_of_birth. Accepts year-only or full date.
 *
 * @param {string} value - Year "YYYY", date "YYYY-MM-DD", or other parseable date
 * @returns {string|null} "YYYY" or null
 */
const normalizeToBirthYear = (value) => {
  if (!value) return null;
  const s = String(value).trim();
  const y = parseYearFromText(s);
  if (y) return y;
  const d = parseDateToYYYYMMDD(s, false);
  if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d.slice(0, 4);
  return null;
};

/**
 * Parses dates from various formats to YYYY-MM-DD format
 * Handles: "March 30, 1991", "7 August 1991", "1991-03-30", "03/30/1991", etc.
 *
 * @param {string} dateValue - Date value in various formats
 * @param {boolean} returnOriginal - If true, returns original value on parse failure; if false, returns null (default: true)
 * @returns {string|null} Normalized date in YYYY-MM-DD format, original value (if returnOriginal=true), or null (if returnOriginal=false)
 */
const parseDateToYYYYMMDD = (dateValue, returnOriginal = true) => {
  if (!dateValue || typeof dateValue !== 'string') {
    return returnOriginal ? dateValue : null;
  }

  const dateString = dateValue.trim();

  // Already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }

  // Try parsing with Date object
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  } catch (e) {
    // Continue to manual parsing
  }

  // Manual parsing for "March 30, 1991" format
  const monthMap = {
    january: '01', jan: '01',
    february: '02', feb: '02',
    march: '03', mar: '03',
    april: '04', apr: '04',
    may: '05',
    june: '06', jun: '06',
    july: '07', jul: '07',
    august: '08', aug: '08',
    september: '09', sep: '09', sept: '09',
    october: '10', oct: '10',
    november: '11', nov: '11',
    december: '12', dec: '12'
  };

  const cleaned = dateString.toLowerCase();
  for (const [monthName, monthNum] of Object.entries(monthMap)) {
    if (cleaned.includes(monthName)) {
      const parts = cleaned.split(monthName);
      if (parts.length > 1) {
        const rest = parts[1].trim().replace(/,/g, '').split(/\s+/);
        const day = parseInt(rest[0]);
        const year = parseInt(rest[rest.length - 1]);
        if (day && year && year > 1900 && year < 2100) {
          return `${year}-${monthNum}-${String(day).padStart(2, '0')}`;
        }
      }
    }
  }

  // Return original value or null based on returnOriginal flag
  return returnOriginal ? dateValue : null;
};

/**
 * Determines if a question ID should map to a specific key format
 * Some questions have special mapping requirements
 *
 * @param {string} questionId - Question ID from chat
 * @returns {string} Mapped onboarding key
 */
const mapQuestionIdToKey = (questionId) => {
  if (!questionId || typeof questionId !== 'string') {
    return questionId;
  }

  // Normalize legacy keys like "children_number"
  if (questionId.includes('-')) {
    const legacyMatch = questionId.match(/^\d+-(\d+_.+)$/);
    if (legacyMatch) {
      return legacyMatch[1];
    }
    return questionId;
  }

  // Current (backend) IDs are already unique and ordered, so use as-is.
  return questionId;
};

/**
 * Processes and normalizes answer value based on question type
 *
 * @param {string} questionId - Question ID
 * @param {string} answerText - Answer text from chat
 * @returns {string|number|Array|Object} Processed answer value
 */
const processAnswerValue = (questionId, answerText) => {
  if (!answerText || typeof answerText !== 'string') {
    return answerText;
  }

  // Special handling for children college entries (backend ID: children_college_years)
  if (questionId === 'children_college_years' || questionId === '2_children_college_years' || questionId.endsWith('_children_college_years')) {
    const parsed = parseChildrenCollegeData(answerText);
    // Return as array - onboardingProgress will handle serialization
    return parsed;
  }

  // Special handling for birth year questions (backend IDs: your_year_of_birth, partner_year_of_birth_of_birth)
  if (questionId === 'your_year_of_birth' || questionId === 'partner_year_of_birth_of_birth' || questionId === '2_your_year_of_birth' || questionId === '2_partner_year_of_birth') {
    const y = parseYearFromText(answerText);
    if (y) return y;
    const d = parseDateToYYYYMMDD(answerText, false);
    if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d.slice(0, 4);
    return answerText.trim();
  }

  // Numeric fields: suffixes from backend question IDs (questionId.includes(field))
  const numericFields = [
    // Category 1
    'monthly_net_income_during_career_shift',
    'monthly_net_income_after_career_shift',
    'monthly_net_income_partner',
    // Category 2
    'children_number',
    'children_college_savings',
    'retirement_age',
    'partner_retire',
    'your_social_security_benefits_age',
    'partner_social_security_benefits_age',
    'pension_income_start_age',
    'pension_monthly_income',
    'annuity_income_start_age',
    'annuity_monthly_income',
    // Category 3
    'total_retirement_account_each_year',
    'monthly_expenses',
    // Category 4
    'retirement_accounts',
    'total_balance_post_tax_retirement_accounts',
    'total_balance_equity_compensation_accounts',
    'other_investments',
    'home_estimated_value',
    'current_market_value',
    // Category 5
    'home_mortgage_debt',
    'car_loan_debt',
    'educational_loan',
    'owe_credit_cards',
    'other_debts_loans'
  ];

  if (numericFields.some(field => questionId.includes(field))) {
    // Extract numeric value but keep as string (onboardingProgress stores numbers as strings)
    const numericValue = parseNumericValue(answerText);
    if (numericValue !== null && !isNaN(numericValue)) {
      // Ensure non-negative for financial values
      return Math.max(0, numericValue).toString();
    }
    // Return "0" as default for numeric fields if parsing fails
    return "0";
  }

  // Return original text for other fields (trimmed)
  return answerText.trim();
};

/**
 * Converts **`v2_onboarding_ai.chat`** (agent Q/A messages) into `onboardingProgress` keys.
 * Uses question IDs from messages directly (e.g., `"family_situation"`).
 *
 * CRITICAL: This function handles:
 * - Back-compat normalization: "family_situation"
 * - Children college parsing: "Child 1: 2022\n..." -> [2022, ...]
 * - Date normalization: "March 30, 1991" -> "1991-03-30"
 * - Numeric extraction: "$1,000" -> "1000"
 *
 * @param {Array} chatData - Messages from `v2_onboarding_ai.chat` (same shape as API/DB).
 * @returns {Object} onboardingProgress object compatible with financial helpers in this module.
 */
const convertV2OnboardingAiChatToOnboardingProgress = (chatData) => {
  const onboardingProgress = {};

  if (!Array.isArray(chatData)) {
    logger.warn('Invalid chat data format');
    return onboardingProgress;
  }

  if (chatData.length === 0) {
    logger.warn('Empty chat data array');
    return onboardingProgress;
  }

  // Sort by timestamp to ensure correct order (latest answers override earlier ones)
  const sortedChat = [...chatData].sort((a, b) => {
    const timeA = a.timestamp || 0;
    const timeB = b.timestamp || 0;
    return timeA - timeB;
  });

  // Track processed questions to handle duplicates (use latest answer)
  const processedQuestions = new Map();

  // Process each answer message and map to onboardingProgress format
  sortedChat.forEach((msg) => {
    if (msg.type === 'answer' && msg.id && msg.text !== undefined && msg.text !== null) {
      try {
        const questionId = msg.id;

        // Skip invalid question IDs
        if (!questionId || typeof questionId !== 'string' || questionId.trim() === '') {
          logger.warn('Skipping answer with invalid question ID');
          return;
        }

        // Map question ID to onboardingProgress key
        const onboardingKey = mapQuestionIdToKey(questionId);

        // Process and normalize the answer value
        const processedValue = processAnswerValue(questionId, String(msg.text));

        // Only store non-null/non-undefined values
        if (processedValue != null) {
          onboardingProgress[onboardingKey] = processedValue;
          processedQuestions.set(onboardingKey, true);
        }
      } catch (error) {
        logger.error('Error processing chat message:', error);
        // Continue processing other messages
      }
    }
  });

  return onboardingProgress;
};



// ============================================================================
// AI FINANCIAL CALCULATION FUNCTIONS
// ============================================================================

/**
 * Normalizes family situation string to a canonical enum-like value for storage.
 * isMarried() uses flexible matching (married/partner/spouse), so normalization
 * here is for consistency and UI, not required for isMarried.
 *
 * @param {string} familySituation - Family situation string from AI or chat
 * @returns {string|null} Normalized family situation string or null if can't determine
 */
const normalizeFamilySituation = (familySituation) => {
  if (!familySituation || typeof familySituation !== 'string') {
    return null;
  }

  const lower = familySituation.toLowerCase().trim();

  // Check for married status
  if (lower.includes('married')) {
    // Check for children
    if (lower.includes('children') || lower.includes('kids') || lower.includes('child')) {
      return "Married with children";
    } else {
      return "Married, no kids";
    }
  }

  // Check for single status
  if (lower.includes('single')) {
    if (lower.includes('children') || lower.includes('kids') || lower.includes('child')) {
      return "Single with children";
    } else {
      return "Single, no kids";
    }
  }

  // Check for divorced status
  if (lower.includes('divorced')) {
    if (lower.includes('children') || lower.includes('kids') || lower.includes('child')) {
      return "Divorced with children";
    } else {
      return "Divorced, no kids";
    }
  }

  // If already in correct format, return as-is
  const validFormats = [
    "Married, no kids",
    "Married with children",
    "Single, no kids",
    "Single with children",
    "Divorced, no kids",
    "Divorced with children"
  ];

  if (validFormats.includes(familySituation)) {
    return familySituation;
  }

  // Default: return null if can't determine
  return null;
};


/**
 * Helper function to extract AI response key from field path
 * Converts a question ID/field path to an AI response key.
 * Examples:
 * - "home_estimated_value" -> "home_estimated_value"
 * - "home_mortgage_debt" -> "home_mortgage_debt"
 * - Legacy: "home_estimated_value" -> "home_estimated_value"
 *
 * @param {string} fieldPath - Question ID / field path
 * @returns {string} Response key without prefix (e.g., "home_estimated_value")
 */
const getResponseKey = (fieldPath) => {
  if (!fieldPath) return '';
  // Remove numeric prefix:
  // - Current: "" (category + order)
  return fieldPath.replace(/^\d+(?:-\d+)?_\d+_/, '');
};

/**
 * Builds a formatted summary of chat data for AI processing
 *
 * @param {Array} chatData - Messages from `v2_onboarding_ai.chat`.
 * @returns {string} Formatted chat summary
 * @throws {Error} If chatData is invalid
 */
const buildChatSummary = (chatData) => {
  if (!chatData || !Array.isArray(chatData)) {
    throw new Error('Invalid chatData: must be an array');
  }

  return chatData
    .filter(msg => msg && (msg.type === 'answer' || msg.type === 'question'))
    .map(msg => {
      const id = msg.id || 'unknown';
      const text = msg.text || '';
      return msg.type === 'question' ? `Q[${id}]: ${text}` : `A[${id}]: ${text}`;
    })
    .join('\n');
};

/**
 * Builds the prompt for extracting all financial data from chat
 *
 * @param {string} chatSummary - Formatted chat summary
 * @returns {{prompt: string, responseFormat: string}} Prompt and response format
 */
const buildAllFinancialDataPrompt = (chatSummary) => {
  const prompt = `You are a financial data extraction specialist. Extract ALL financial and personal information from a financial planning conversation.

  ${AI_RESPONSE_INSTRUCTIONS}

  CRITICAL INSTRUCTIONS:
  1. Match question IDs exactly from the chat conversation
  2. Extract answers from the conversation based on question IDs
  3. Convert all data to the exact formats specified below
  4. Return ONLY valid JSON - no explanations, no markdown

  EXACT QUESTION IDs TO FIND (must match backend question IDs):

  CATEGORY 1 — Self Assessment and Career Shift Readiness:
  - "duration" -> Extract duration, convert to standard format: "X months" or "X year(s)"
  - "monthly_net_income_during_career_shift" -> Extract individual estimated monthly net income during career shift period (numeric, monthly amount). Return as: monthly_net_income_before_career_shift
  - "monthly_net_income_after_career_shift" -> Extract individual estimated monthly net income after career shift period once income stabilizes (numeric, monthly amount). Return as: monthly_net_income_after_career_shift (individual only, not household)

  CATEGORY 2 — Personal & Background Info:
  - "family_situation" -> Extract family situation and return EXACTLY one of these strings (case-sensitive):
    * "Married, no kids" - if user is married without children
    * "Married with children" - if user is married with children
    * "Single, no kids" - if user is single without children
    * "Single with children" - if user is single with children
    * "Divorced, no kids" - if user is divorced without children
    * "Divorced with children" - if user is divorced with children
    CRITICAL: Return the exact string format shown above. The system uses exact string matching.
  - "your_year_of_birth" -> Extract birth year only as 4-digit YYYY (e.g., "March 30, 1991" or "1991" -> "1991")
  - "partner_year_of_birth_of_birth" -> Extract spouse/partner birth year as 4-digit YYYY if married, or null if not married
  - "monthly_net_income_partner" -> Extract spouse/partner net monthly income if married (numeric or option like $0, $1K, $5K, $10K, $15K, $20K, $25K; 0 if not married)
  - "children_number" -> Extract number of children (numeric)
  - "children_college_years" -> Extract college entry years in format: [number, number, ...]
    Parse formats like "Child 1: 2022\\nChild 2: 2030" (years only)
  - "children_college_savings" -> Extract total college savings/funds allocated (numeric; also in assets below)
  - "housing_situation" -> Extract housing situation (backend options): "Rent" | "Own" | "In Transition"
    Return as: housing_situation
  - "retirement_age" -> Extract user retirement age (numeric)
    Return as: retirement_age
  - "partner_retire" -> Extract spouse retirement age if married (numeric, or null if not married)
    Return as: partner_retire_age
  - "your_social_security_benefits_age" -> Extract user social security age (numeric, must be >= 62, default 67)
    Return as: social_security_ages.user
  - "partner_social_security_benefits_age" -> Extract spouse social security age if married (numeric, must be >= 62, default 67, or null if not married)
    Return as: social_security_ages.spouse
  - "pension_plan" -> Extract pension plan status: "Yes" or "No"
    Return as: pension.plan
  - "pension_income_start_age" -> Extract age when pension income starts (numeric, only if pension_plan is "Yes", default 0 or null)
    Return as: pension.income_start_age
  - "pension_monthly_income" -> Extract monthly pension income amount (numeric, only if pension_plan is "Yes", default 0 or null)
    Return as: pension.monthly_income
  - "annuity_plan" -> Extract annuity plan status: "Yes" or "No"
    Return as: annuity.plan
  - "annuity_income_start_age" -> Extract age when annuity income starts (numeric, only if annuity_plan is "Yes", default 0 or null)
    Return as: annuity.income_start_age
  - "annuity_monthly_income" -> Extract monthly annuity income amount (numeric, only if annuity_plan is "Yes", default 0 or null)
    Return as: annuity.monthly_income
  - "state" -> Extract state location (string, e.g., "California")
    Return as: state
  - "city" -> Extract city location (string, e.g., "San Diego")
    Return as: city

  CATEGORY 3 — Income and Expenses:
  - "total_retirement_account_each_year" -> Extract how much you and your employer put into retirement account each year (numeric, annual amount)
  - "monthly_expenses" -> Extract monthly expenses (numeric)

  CATEGORY 4 — Investments and Assets (return in assets object):
  - "children_college_savings" -> assets.children_college_savings
  - "retirement_accounts" -> assets.retirement_accounts
  - "total_balance_post_tax_retirement_accounts" -> assets.total_balance_post_tax_retirement_accounts
  - "total_balance_equity_compensation_accounts" -> assets.total_balance_equity_compensation_accounts
  - "other_investments" -> assets.other_investments
  - "home_estimated_value" -> assets.home_estimated_value
  - "current_market_value" -> assets.current_market_value

  CATEGORY 5 — Debts and Liabilities (return in debts object):
  - "home_mortgage_debt" -> debts.home_mortgage_debt
  - "car_loan_debt" -> debts.car_loan_debt
  - "educational_loan" -> debts.educational_loan
  - "owe_credit_cards" -> debts.owe_credit_cards
  - "other_debts_loans" -> debts.other_debts_loans

  BIRTH YEAR (your_year_of_birth, partner_year_of_birth_of_birth) INSTRUCTIONS:
  - Extract only the 4-digit birth year (YYYY), e.g. 1900-2100
  - "March 30, 1991" -> "1991", "born in 1985" -> "1985", "1991" -> "1991"

  NUMERIC EXTRACTION RULES:
  - Remove ALL non-numeric characters except decimal points: $, commas, spaces, currency symbols
  - Convert to pure numbers (not strings): "1000" -> 1000, "$1,000" -> 1000
  - Handle suffixes: "1000k" or "1.5k" -> 1000000 or 1500, "1M" -> 1000000
  - Default missing values to 0 (not null)

  FAMILY SITUATION CRITICAL:
  - You MUST return EXACTLY one of these 6 strings (copy exactly, including capitalization and punctuation):
    * "Married, no kids" (if married without children)
    * "Married with children" (if married with children)
    * "Single, no kids" (if single without children)
    * "Single with children" (if single with children)
    * "Divorced, no kids" (if divorced without children)
    * "Divorced with children" (if divorced with children)
  - Look for keywords: "married", "single", "divorced", "children", "kids"
  - If user says "married", check for children mentioned separately

  CHILDREN COLLEGE FORMAT:
  - Extract from question ID "children_college_years"
  - If format is "Child 1: 2022\\nChild 2: 2030", convert to:
    [2022, 2030]
  - year must be 4-digit number between 2000-2100
  - Do NOT include amountPaid (this project now collects years only)

  BIRTH YEAR FORMAT (your_year_of_birth, partner_year_of_birth_of_birth):
  - Return ONLY the 4-digit year (YYYY), e.g. "1991", "1985"
  - partner_yob: "YYYY" if married, null if not married

  Chat conversation:
  ${chatSummary}

  VALIDATION CHECKLIST BEFORE RETURNING:
  ✓ All numeric values are numbers (not strings)
  ✓ your_yob and partner_yob are 4-digit year (YYYY) or null
  ✓ family_situation is EXACTLY one of the 6 valid strings
  ✓ children_college_years is array of years (2000-2100)
  ✓ All required fields are present

  Return ONLY the raw JSON object with all extracted data.`;

  const responseFormat = `{
    "your_yob": "YYYY",
    "retirement_age": number,
    "partner_retire_age": number or null,
    "family_situation": "EXACTLY one of: 'Married, no kids' | 'Married with children' | 'Single, no kids' | 'Single with children' | 'Divorced, no kids' | 'Divorced with children'",
    "partner_yob": "YYYY or null",
    "children_number": number,
    "children_college_years": [number],
    "housing_situation": "Rent" | "Own" | "In Transition" or null,
    "duration": "string like '1 year' or '6 months'",
    "monthly_net_income_before_career_shift": number,
    "monthly_net_income_after_career_shift": number,
    "career_change": "Voluntary" or "Necessary",
    "career_change_necessary_considering": "Already" or "Considering it for the future" or null,
    "monthly_household_income": number,
    "monthly_net_income_partner": number,
    "total_retirement_account_each_year": number,
    "monthly_expenses": number,
    "state": "string or null",
    "city": "string or null",
    "assets": {
      "children_college_savings": number,
      "retirement_accounts": number,
      "total_balance_post_tax_retirement_accounts": number,
      "total_balance_equity_compensation_accounts": number,
      "other_investments": number,
      "home_estimated_value": number,
      "current_market_value": number
    },
    "debts": {
      "home_mortgage_debt": number,
      "car_loan_debt": number,
      "educational_loan": number,
      "owe_credit_cards": number,
      "other_debts_loans": number
    },
    "social_security_ages": {
      "user": number,
      "spouse": number or null
    },
    "pension": {
      "plan": "Yes" or "No",
      "income_start_age": number or null,
      "monthly_income": number or null
    },
    "annuity": {
      "plan": "Yes" or "No",
      "income_start_age": number or null,
      "monthly_income": number or null
    }
  }`;

  return { prompt, responseFormat };
};

/**
 * Extracts response text from various API response formats
 *
 * @param {Object} data - API response data
 * @returns {string|Object} Extracted response text or parsed object
 * @throws {Error} If response format is unexpected
 */
const extractResponseText = (data) => {
  // Check if already a parsed object (not an array)
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    // Check if it looks like a direct JSON response (has expected keys)
    const expectedKeys = ['your_yob', 'retirement_age', 'family_situation', 'assets', 'debts'];
    const hasExpectedKeys = expectedKeys.some(key => key in data);
    if (hasExpectedKeys) {
      return data;
    }
  }

  // Try various response format patterns
  const responseText =
    data?.data?.answer ||
    data?.response ||
    data?.text ||
    data?.message ||
    data?.answer ||
    (typeof data === 'string' ? data : null);

  if (!responseText) {
    logger.error('Unexpected API response format', { data });
    throw new Error('Unexpected API response format');
  }

  return responseText;
};

/**
 * Cleans markdown code blocks from response text
 *
 * @param {string} text - Response text that may contain markdown
 * @returns {string} Cleaned text without markdown code blocks
 */
const cleanMarkdownCodeBlocks = (text) => {
  let cleaned = text.trim();

  if (cleaned.startsWith('```json')) {
    cleaned = cleaned.replace(/^```json\s*/, '').replace(/\s*```$/, '');
  } else if (cleaned.startsWith('```')) {
    cleaned = cleaned.replace(/^```\s*/, '').replace(/\s*```$/, '');
  }

  return cleaned;
};

/**
 * Parses JSON from AI response text with fallback strategies
 *
 * @param {string} responseText - Response text from AI
 * @returns {Object} Parsed JSON object
 * @throws {Error} If JSON cannot be parsed
 */
const parseAIResponseJSON = (responseText) => {
  const cleanedText = cleanMarkdownCodeBlocks(responseText);

  // Try direct parsing first
  try {
    return JSON.parse(cleanedText);
  } catch (parseError) {
    // If direct parse fails, try to extract JSON object from text
    const jsonMatch = cleanedText.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      try {
        return JSON.parse(jsonMatch[0]);
      } catch (extractError) {
        logger.error('Error parsing extracted JSON', { extractError, jsonMatch: jsonMatch[0] });
        throw new Error('Could not parse JSON from AI response');
      }
    }

    logger.error('No JSON object found in AI response', { responseText: cleanedText.substring(0, 200) });
    throw new Error('Could not find JSON object in AI response');
  }
};

/**
 * Calls the AI API endpoint
 *
 * @param {string} endpoint - API endpoint path
 * @param {string} token - Authentication token
 * @param {string} prompt - Full prompt to send to AI
 * @returns {Promise<Object>} API response data
 * @throws {Error} If API call fails
 */
const callAIAPI = async (endpoint, token, prompt) => {
  const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: getApiHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      token,
      question: `${prompt} Remember: Return ONLY the raw JSON object, no markdown, no explanations, no code blocks.`
    })
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    throw new Error(`AI API error: ${response.status} - ${errorText}`);
  }

  return response.json();
};

/**
 * Builds the full prompt with response format instructions
 *
 * @param {string} prompt - Base prompt
 * @param {string} responseFormat - Expected response format
 * @returns {string} Full prompt with format instructions
 */
const buildFullPrompt = (prompt, responseFormat) => {
  return `${prompt}\n\nIMPORTANT: Return ONLY a valid JSON object in this exact format:\n${responseFormat}\n\nDo not include any explanation, markdown formatting, or additional text. Only return the raw JSON object.`;
};

/**
 * Gets the appropriate API endpoint based on options
 *
 * @param {Object} options - Options object
 * @returns {string} API endpoint path
 */
const getAPIEndpoint = (options) => {
  const useAdminEndpoint = options.useAdminEndpoint || false;
  return useAdminEndpoint
    ? config.API_ENDPOINTS.ADMIN_CHAT
    : config.API_ENDPOINTS.ONBOARDING_AI_CHAT;
};

/**
 * Gets authentication token from localStorage
 *
 * @returns {string} Authentication token
 * @throws {Error} If token is not found
 */
const getAuthToken = () => {
  const token = localStorage.getItem(config.STORAGE_KEYS.TOKEN);
  if (!token) {
    throw new Error('No authentication token found');
  }
  return token;
};

/**
 * Builds prompt based on calculation type
 *
 * @param {string} calculationType - Type of calculation to perform
 * @param {string} chatSummary - Formatted chat summary
 * @returns {{prompt: string, responseFormat: string}} Prompt and response format
 * @throws {Error} If calculation type is unknown
 */
const buildPromptForCalculationType = (calculationType, chatSummary) => {
  switch (calculationType) {
    case 'all_financial_data':
      return buildAllFinancialDataPrompt(chatSummary);
    default:
      throw new Error(`Unknown calculation type: ${calculationType}`);
  }
};

/**
 * Calls AI API to calculate financial values from chat data
 *
 * @param {Array} chatData - Messages from `v2_onboarding_ai.chat`.
 * @param {string} calculationType - Type of calculation to perform
 * @param {Object} options - Additional options for the calculation
 * @returns {Promise<Object>} Calculated values as JSON object
 */
const callAIFinancialCalculation = async (chatData, calculationType, options = {}) => {
  try {
    // Validate inputs and get token
    const token = getAuthToken();
    const chatSummary = buildChatSummary(chatData);
    const endpoint = getAPIEndpoint(options);

    // Build prompt based on calculation type
    const { prompt, responseFormat } = buildPromptForCalculationType(calculationType, chatSummary);
    const fullPrompt = buildFullPrompt(prompt, responseFormat);

    // Call AI API
    const apiResponse = await callAIAPI(endpoint, token, fullPrompt);

    // Extract and parse response
    const responseTextOrObject = extractResponseText(apiResponse);

    // If already an object, return it
    if (typeof responseTextOrObject === 'object') {
      return responseTextOrObject;
    }

    // Otherwise parse JSON from text
    return parseAIResponseJSON(responseTextOrObject);
  } catch (error) {
    logger.error(`Error calling AI for ${calculationType}:`, error);
    throw error;
  }
};

/**
 * Resolves a numeric value from onboarding progress by trying primary key then alternate keys.
 * Supports both standard question IDs (e.g. monthly_net_income_during_career_shift) and
 * AI-style keys (e.g. monthly_net_income_before_career_shift, or nested assets.other_investments).
 *
 * @param {Object} progress - Onboarding progress or AI result object
 * @param {string} primaryKey - Primary key (e.g. "monthly_net_income_during_career_shift")
 * @param {number} defaultValue - Value to return when none found
 * @param {Array<string>} [alternateKeys] - Alternate keys to try (e.g. ["monthly_net_income_before_career_shift"] or ["assets", "other_investments"] for nested)
 * @returns {number}
 */
function getProgressNumeric(progress, primaryKey, defaultValue = 0, alternateKeys = []) {
  if (!progress || typeof progress !== 'object') return defaultValue;
  const fromPrimary = parseNumericValue(progress[primaryKey], null);
  if (fromPrimary != null && !Number.isNaN(fromPrimary)) return fromPrimary;
  for (const key of alternateKeys) {
    if (key.includes('.')) {
      const parts = key.split('.');
      let v = progress;
      for (const p of parts) {
        v = v?.[p];
        if (v === undefined) break;
      }
      const num = parseNumericValue(v, null);
      if (num != null && !Number.isNaN(num)) return num;
    } else {
      const num = parseNumericValue(progress[key], null);
      if (num != null && !Number.isNaN(num)) return num;
    }
  }
  return defaultValue;
}

/**
 * Calculates Career Shift Runway metrics
 *
 * This function calculates all Career Shift Runway values based on onboarding progress data.
 * Uses both standard question-ID keys and AI-extraction keys so values are correct whether
 * progress comes from merged onboardingProgress or raw AI result.
 *
 * @param {Object} onboardingProgress - Onboarding progress data object (or AI result with monthly_net_income_before_career_shift, monthly_expenses, assets, etc.)
 * @returns {Object} Career Shift Runway data object with:
 *   - currentMonthlyIncome: number (individual estimated monthly net income during career shift period, from monthly_net_income_during_career_shift)
 *   - currentMonthlyExpenses: number
 *   - monthlyGap: number
 *   - careerShiftDurationMonths: number
 *   - runwayValue: string (numeric string or "Fully Covered")
 *   - runwayDisplayValue: string (formatted display value with "months" or "Fully Covered")
 *   - accessibleShortTermLiquidity: number
 */
export const getCareerShiftRunwayData = (onboardingProgress) => {
  try {
    if (!onboardingProgress || typeof onboardingProgress !== 'object') {
      return {
        currentMonthlyIncome: 0,
        currentMonthlyExpenses: 0,
        monthlyGap: 0,
        careerShiftDurationMonths: 0,
        runwayValue: FINANCIAL_CONSTANTS.FULLY_COVERED,
        runwayDisplayValue: FINANCIAL_CONSTANTS.FULLY_COVERED,
        accessibleShortTermLiquidity: 0
      };
    }

    // Income during career shift: standard key first, then AI-style (monthly_net_income_before_career_shift)
    let monthlyNetIncomeDuringCareerShift = getProgressNumeric(
      onboardingProgress,
      'monthly_net_income_during_career_shift',
      0,
      ['monthly_net_income_before_career_shift']
    );
    const familySituationRunway = onboardingProgress['family_situation'] ?? onboardingProgress.family_situation;
    if (isMarried(familySituationRunway)) {
      monthlyNetIncomeDuringCareerShift += getProgressNumeric(
        onboardingProgress,
        'monthly_net_income_partner',
        0,
        ['monthly_net_income_partner']
      );
    }
    const currentMonthlyExpenses = getProgressNumeric(
      onboardingProgress,
      'monthly_expenses',
      0,
      ['monthly_expenses']
    );

    // Monthly Gap / Surplus calculation
    // Show actual difference: negative for gap (expenses > income), positive for surplus (income > expenses)
    // Example: Expenses $5000, Income $1000 → Gap = -$4000
    // Example: Expenses $3000, Income $5000 → Surplus = $2000
    const monthlyGap = currentMonthlyExpenses - monthlyNetIncomeDuringCareerShift;

    // Career Shift Duration: use parseDurationToMonths helper
    const careerShiftDurationMonths = parseDurationToMonths(onboardingProgress);
    // Validate duration is a valid number
    const validDurationMonths = (typeof careerShiftDurationMonths === 'number' && !isNaN(careerShiftDurationMonths) && isFinite(careerShiftDurationMonths) && careerShiftDurationMonths >= 0)
      ? careerShiftDurationMonths
      : 0;

    // Accessible Short-Term Liquidity: standard keys first, then AI-style (top-level or assets.*)
    const careerShiftOtherInvestments = getProgressNumeric(
      onboardingProgress,
      'other_investments',
      0,
      ['other_investments', 'assets.other_investments']
    );
    const careerShiftRetirementAccounts = getProgressNumeric(
      onboardingProgress,
      'retirement_accounts',
      0,
      ['retirement_accounts', 'assets.retirement_accounts']
    );

    // Calculate retirement loan amount with validation
    let retirementLoanAmount = 0;
    if (careerShiftRetirementAccounts >= 100000) {
      retirementLoanAmount = 50000;
    } else if (careerShiftRetirementAccounts > 0) {
      retirementLoanAmount = Math.round(careerShiftRetirementAccounts * 0.5);
    }

    const accessibleShortTermLiquidity = careerShiftOtherInvestments + retirementLoanAmount;
    // Validate liquidity is a valid number
    const validLiquidity = (typeof accessibleShortTermLiquidity === 'number' && !isNaN(accessibleShortTermLiquidity) && isFinite(accessibleShortTermLiquidity))
      ? Math.max(0, accessibleShortTermLiquidity)
      : 0;

    // Runway calculation
    // IF Monthly Gap / Surplus <= $0 (surplus or zero) OR Accessible Short-Term Liquidity <= $0:
    //   Runway = "Fully Covered"
    // ELSE IF Monthly Gap / Surplus > $0 (gap) AND Accessible Short-Term Liquidity > $0:
    //   Runway (months) = Accessible Short-Term Liquidity ÷ Monthly Gap / Surplus
    let runwayValue = FINANCIAL_CONSTANTS.FULLY_COVERED;
    let runwayDisplayValue = FINANCIAL_CONSTANTS.FULLY_COVERED;
    let runwayMonthsNumeric = Infinity;

    if (monthlyGap <= 0 || validLiquidity <= 0) {
      // Fully Covered: surplus (monthlyGap <= 0) or no liquidity
      runwayMonthsNumeric = Infinity;
    } else {
      // Calculate runway: gap exists (monthlyGap > 0) and liquidity available (validLiquidity > 0)
      runwayMonthsNumeric = validLiquidity / monthlyGap;
      // Validate runway calculation result
      if (typeof runwayMonthsNumeric === 'number' && !isNaN(runwayMonthsNumeric) && isFinite(runwayMonthsNumeric) && runwayMonthsNumeric > 0) {
        // Format: remove .0 decimal, keep other decimals (round first to avoid float drift)
        const roundedMonths = Math.round(runwayMonthsNumeric * 10) / 10;
        const formattedMonths = roundedMonths % 1 === 0
          ? String(Math.round(roundedMonths))
          : roundedMonths.toFixed(1);
        runwayValue = formattedMonths;
        runwayDisplayValue = `${formattedMonths} months`;
      } else {
        // Invalid calculation result, default to Fully Covered
        runwayMonthsNumeric = Infinity;
      }
    }

    return {
      currentMonthlyIncome: monthlyNetIncomeDuringCareerShift,
      currentMonthlyExpenses,
      monthlyGap,
      careerShiftDurationMonths: validDurationMonths,
      runwayValue,
      runwayDisplayValue,
      accessibleShortTermLiquidity: validLiquidity
    };
  } catch (error) {
    logger.error('❌ Error calculating Career Shift Runway:', error);
    // Set default values to ensure the section still shows
    return {
      currentMonthlyIncome: 0,
      currentMonthlyExpenses: 0,
      monthlyGap: 0,
      careerShiftDurationMonths: 0,
      runwayValue: FINANCIAL_CONSTANTS.FULLY_COVERED,
      runwayDisplayValue: FINANCIAL_CONSTANTS.FULLY_COVERED,
      accessibleShortTermLiquidity: 0
    };
  }
};

/**
 * Extracts financial fields from **`v2_onboarding_ai.chat`** via the AI extraction endpoint,
 * merged with a baseline from parsing the chat locally (`convertV2OnboardingAiChatToOnboardingProgress`).
 *
 * @param {Array} chatData - Messages from `v2_onboarding_ai.chat`.
 * @param {Object} [options] - Options forwarded to the AI request (e.g. endpoint overrides).
 * @returns {Promise<Object>} Object with `onboardingProgress` and calculated text fields (duration, healthcare, etc.).
 */
export const calculateAllFinancialDataAI = async (chatData, options = {}) => {
  const LIFE_EXPECTANCY = FINANCIAL_CONSTANTS.LIFE_EXPECTANCY;

  try {
    // Validate chatData
    if (!chatData || !Array.isArray(chatData)) {
      logger.error('Invalid chatData provided to calculateAllFinancialDataAI');
      throw new Error('chatData must be a non-empty array');
    }

    // FIRST: Convert chat data to get baseline onboardingProgress
    // This ensures we have fallback data even if AI extraction fails for some fields
    let onboardingProgress = convertV2OnboardingAiChatToOnboardingProgress(chatData);

    // Try AI to get all data at once
    const aiResult = await callAIFinancialCalculation(chatData, 'all_financial_data', options);

    if (aiResult && typeof aiResult === 'object') {
      // Merge AI result into onboardingProgress (AI data takes precedence; baseline from convertV2OnboardingAiChatToOnboardingProgress provides fallbacks).
      // Mapping is by field key (e.g. `your_yob` -> `your_year_of_birth`, `partner_yob` -> `partner_year_of_birth_of_birth`,
      // `family_situation`, `children_number`, retirement timing keys, `duration`, income/expenses keys, and assets/debts source fields).

      // Validate AI result structure
      if (Array.isArray(aiResult)) {
        logger.error('AI result is an array, expected object');
        throw new Error('Invalid AI result format: expected object, got array');
      }

      // Personal Information (2_your_year_of_birth: birth year YYYY only)
      if (aiResult.your_yob) {
        const normalized = normalizeToBirthYear(aiResult.your_yob) || String(aiResult.your_yob).trim();
        if (/^\d{4}$/.test(normalized)) {
          const y = parseInt(normalized, 10);
          if (y >= 1900 && y <= 2100) onboardingProgress["your_year_of_birth"] = normalized;
        }
      }

      if (aiResult.retirement_age !== undefined && aiResult.retirement_age !== null) {
        const retirementAge = typeof aiResult.retirement_age === 'number'
          ? aiResult.retirement_age
          : parseInt(aiResult.retirement_age);
        if (!isNaN(retirementAge) && retirementAge > 0 && retirementAge <= 100) {
          onboardingProgress["retirement_age"] = retirementAge.toString();
        }
      }

      if (aiResult.partner_retire_age !== undefined && aiResult.partner_retire_age !== null) {
        const partnerRetireAge = typeof aiResult.partner_retire_age === 'number'
          ? aiResult.partner_retire_age
          : parseInt(aiResult.partner_retire_age);
        if (!isNaN(partnerRetireAge) && partnerRetireAge > 0 && partnerRetireAge <= 100) {
          onboardingProgress["partner_retire"] = partnerRetireAge.toString();
        }
      }

      // Normalize family_situation for consistent storage; isMarried() uses flexible
      // matching (married/partner/spouse). Also check children_number for kids.
      if (aiResult.family_situation) {
        let normalizedFamilySituation = normalizeFamilySituation(aiResult.family_situation);

        // If normalization didn't determine children status, check children_number
        if (normalizedFamilySituation) {
          const lower = normalizedFamilySituation.toLowerCase();
          // If it's a married/single/divorced status but we need to check children
          if ((lower.includes('married') || lower.includes('single') || lower.includes('divorced')) &&
            !lower.includes('children') && !lower.includes('kids')) {
            // Check if we have children_number to determine if they have children
            const childrenNumber = aiResult.children_number !== undefined
              ? (typeof aiResult.children_number === 'number' ? aiResult.children_number : parseInt(aiResult.children_number))
              : (onboardingProgress["children_number"] ? parseInt(onboardingProgress["children_number"]) : 0);

            if (!isNaN(childrenNumber) && childrenNumber > 0) {
              // User has children, update family situation
              if (lower.includes('married')) {
                normalizedFamilySituation = "Married with children";
              } else if (lower.includes('single')) {
                normalizedFamilySituation = "Single with children";
              } else if (lower.includes('divorced')) {
                normalizedFamilySituation = "Divorced with children";
              }
            }
          }

          onboardingProgress["family_situation"] = normalizedFamilySituation;
        } else {
          // AI provided family_situation but normalization failed - try to normalize baseline if exists
          if (onboardingProgress["family_situation"]) {
            let normalizedBaseline = normalizeFamilySituation(onboardingProgress["family_situation"]);
            if (normalizedBaseline) {
              // Also check children_number for baseline
              const childrenNumber = onboardingProgress["children_number"]
                ? parseInt(onboardingProgress["children_number"])
                : 0;
              if (!isNaN(childrenNumber) && childrenNumber > 0) {
                const lower = normalizedBaseline.toLowerCase();
                if (lower.includes('married') && !lower.includes('children')) {
                  normalizedBaseline = "Married with children";
                } else if (lower.includes('single') && !lower.includes('children')) {
                  normalizedBaseline = "Single with children";
                } else if (lower.includes('divorced') && !lower.includes('children')) {
                  normalizedBaseline = "Divorced with children";
                }
              }
              onboardingProgress["family_situation"] = normalizedBaseline;
            }
          }
          logger.warn('AI family_situation normalization failed');
        }
      } else {
        // AI didn't provide family_situation - normalize baseline if it exists
        if (onboardingProgress["family_situation"]) {
          let normalizedBaseline = normalizeFamilySituation(onboardingProgress["family_situation"]);
          if (normalizedBaseline) {
            // Also check children_number for baseline
            const childrenNumber = onboardingProgress["children_number"]
              ? parseInt(onboardingProgress["children_number"])
              : 0;
            if (!isNaN(childrenNumber) && childrenNumber > 0) {
              const lower = normalizedBaseline.toLowerCase();
              if (lower.includes('married') && !lower.includes('children')) {
                normalizedBaseline = "Married with children";
              } else if (lower.includes('single') && !lower.includes('children')) {
                normalizedBaseline = "Single with children";
              } else if (lower.includes('divorced') && !lower.includes('children')) {
                normalizedBaseline = "Divorced with children";
              }
            }
            onboardingProgress["family_situation"] = normalizedBaseline;
          }
        }
      }

      if (aiResult.partner_yob) {
        const normalized = normalizeToBirthYear(aiResult.partner_yob) || String(aiResult.partner_yob).trim();
        if (/^\d{4}$/.test(normalized)) {
          const y = parseInt(normalized, 10);
          if (y >= 1900 && y <= 2100) onboardingProgress["partner_year_of_birth_of_birth"] = normalized;
        }
      }

      if (aiResult.children_number !== undefined) {
        onboardingProgress["children_number"] = aiResult.children_number.toString();
      }

      // Normalize children_college_years to per-child free-text entries (string|null)
      // Back-compat: accept older key children_college as well.
      const aiCollegeYears = aiResult.children_college_years ?? aiResult.children_college;
      if (aiCollegeYears !== undefined && aiCollegeYears !== null) {
        const normalizedEntries = parseChildrenCollegeData(aiCollegeYears);
        if (normalizedEntries.length > 0 || Array.isArray(aiCollegeYears)) {
          onboardingProgress["children_college_years"] = normalizedEntries;
        }
      } else {
        // If AI didn't provide college years, normalize baseline if it exists
        const baselineCollege = onboardingProgress["children_college_years"];
        if (baselineCollege) {
          const normalizedEntries = parseChildrenCollegeData(baselineCollege);
          if (normalizedEntries.length > 0) {
            onboardingProgress["children_college_years"] = normalizedEntries;
          }
        }
      }

      // Career Shift - Validate duration format
      if (aiResult.duration) {
        const duration = String(aiResult.duration).trim();
        // Validate duration is a reasonable string format
        if (duration.length > 0 && duration.length < 50) {
          onboardingProgress["duration"] = duration;
        }
      }

      // Career Shift - individual estimated monthly net income during career shift period
      // Only overwrite baseline when AI returns a positive value; preserve chat-derived value if AI is 0 or missing
      if (aiResult.monthly_net_income_before_career_shift !== undefined && aiResult.monthly_net_income_before_career_shift !== null) {
        const monthlyNetIncomeDuringCareerShift = typeof aiResult.monthly_net_income_before_career_shift === 'number'
          ? aiResult.monthly_net_income_before_career_shift
          : parseNumericValue(aiResult.monthly_net_income_before_career_shift) || 0;
        const baselineDuring = parseNumericValue(onboardingProgress["monthly_net_income_during_career_shift"]) || 0;
        if (monthlyNetIncomeDuringCareerShift > 0 || baselineDuring === 0) {
          onboardingProgress["monthly_net_income_during_career_shift"] = Math.max(0, monthlyNetIncomeDuringCareerShift).toString();
        }
      }

      // Income after career shift - prefer monthly_net_income_after_career_shift, fallback to monthly_household_income
      // Only overwrite baseline when AI returns a positive value; preserve chat-derived value if AI is 0 or missing
      const aiIncomeAfter = aiResult.monthly_net_income_after_career_shift ?? aiResult.monthly_household_income;
      if (aiIncomeAfter !== undefined && aiIncomeAfter !== null) {
        const monthlyIncomeAfter = typeof aiIncomeAfter === 'number'
          ? aiIncomeAfter
          : parseNumericValue(aiIncomeAfter) || 0;
        const baselineAfter = parseNumericValue(onboardingProgress["monthly_net_income_after_career_shift"]) || 0;
        if (monthlyIncomeAfter > 0 || baselineAfter === 0) {
          onboardingProgress["monthly_net_income_after_career_shift"] = Math.max(0, monthlyIncomeAfter).toString();
        }
      }

      // `monthly_net_income_partner` is only relevant when show_if is met (family_situation in ['Married', 'Partnered']); isMarried() covers both
      if (aiResult.monthly_net_income_partner !== undefined && aiResult.monthly_net_income_partner !== null) {
        const familySituation = onboardingProgress["family_situation"];
        if (isMarried(familySituation)) {
          const partnerIncome = typeof aiResult.monthly_net_income_partner === 'number'
            ? aiResult.monthly_net_income_partner
            : parseNumericValue(aiResult.monthly_net_income_partner) || 0;
          onboardingProgress["monthly_net_income_partner"] = Math.max(0, partnerIncome).toString();
        }
      }

      if (aiResult.monthly_expenses !== undefined && aiResult.monthly_expenses !== null) {
        const monthlyExpenses = typeof aiResult.monthly_expenses === 'number'
          ? aiResult.monthly_expenses
          : parseNumericValue(aiResult.monthly_expenses) || 0;
        onboardingProgress["monthly_expenses"] = Math.max(0, monthlyExpenses).toString();
      }

      // Total retirement account contribution each year
      if (aiResult.total_retirement_account_each_year !== undefined && aiResult.total_retirement_account_each_year !== null) {
        const annualContribution = typeof aiResult.total_retirement_account_each_year === 'number'
          ? aiResult.total_retirement_account_each_year
          : parseNumericValue(aiResult.total_retirement_account_each_year) || 0;
        onboardingProgress["total_retirement_account_each_year"] = Math.max(0, annualContribution).toString();
      }

      // Assets - Dynamically process all fields from ASSET_SOURCE_FIELDS in order
      // Uses the same order as defined in helper.js ASSET_SOURCE_FIELDS constant
      // Validate all asset values are numbers and non-negative
      if (aiResult.assets && typeof aiResult.assets === 'object') {
        ASSET_SOURCE_FIELDS.forEach((fieldPath) => {
          // Convert field path to AI response key (e.g., "home_estimated_value" -> "home_estimated_value")
          const responseKey = getResponseKey(fieldPath);

          // Extract value from AI response if present
          if (aiResult.assets[responseKey] !== undefined && aiResult.assets[responseKey] !== null) {
            const value = typeof aiResult.assets[responseKey] === 'number'
              ? aiResult.assets[responseKey]
              : parseNumericValue(aiResult.assets[responseKey]) || 0;
            onboardingProgress[fieldPath] = Math.max(0, value).toString();
          }
        });
      }

      // Debts - Dynamically process all fields from DEBT_SOURCE_FIELDS in order
      // Uses the same order as defined in helper.js DEBT_SOURCE_FIELDS constant
      // Validate all debt values are numbers and non-negative
      if (aiResult.debts && typeof aiResult.debts === 'object') {
        DEBT_SOURCE_FIELDS.forEach((fieldPath) => {
          // Convert field path to AI response key (e.g., "home_mortgage_debt" -> "home_mortgage_debt")
          const responseKey = getResponseKey(fieldPath);

          // Extract value from AI response if present
          if (aiResult.debts[responseKey] !== undefined && aiResult.debts[responseKey] !== null) {
            const value = typeof aiResult.debts[responseKey] === 'number'
              ? aiResult.debts[responseKey]
              : parseNumericValue(aiResult.debts[responseKey]) || 0;
            onboardingProgress[fieldPath] = Math.max(0, value).toString();
          }
        });
      }

      // Social Security - Validate ages are >= 62 (minimum claiming age, no upper limit)
      if (aiResult.social_security_ages && typeof aiResult.social_security_ages === 'object') {
        if (aiResult.social_security_ages.user !== undefined && aiResult.social_security_ages.user !== null) {
          const userSSAge = typeof aiResult.social_security_ages.user === 'number'
            ? aiResult.social_security_ages.user
            : parseInt(aiResult.social_security_ages.user);
          if (!isNaN(userSSAge) && userSSAge >= 62 && userSSAge <= 100) {
            onboardingProgress["your_social_security_benefits_age"] = userSSAge.toString();
          }
        }
        if (aiResult.social_security_ages.spouse !== undefined && aiResult.social_security_ages.spouse !== null) {
          const spouseSSAge = typeof aiResult.social_security_ages.spouse === 'number'
            ? aiResult.social_security_ages.spouse
            : parseInt(aiResult.social_security_ages.spouse);
          if (!isNaN(spouseSSAge) && spouseSSAge >= 62 && spouseSSAge <= 100) {
            onboardingProgress["partner_social_security_benefits_age"] = spouseSSAge.toString();
          }
        }
      }

      // Pension - Extract pension plan information
      if (aiResult.pension && typeof aiResult.pension === 'object') {
        // Set pension plan status
        if (aiResult.pension.plan !== undefined && aiResult.pension.plan !== null) {
          const pensionPlan = String(aiResult.pension.plan).trim();
          if (pensionPlan.toLowerCase() === 'yes' || pensionPlan.toLowerCase() === 'y') {
            onboardingProgress["pension_plan"] = "Yes";

            // Set pension start age if provided
            if (aiResult.pension.income_start_age !== undefined && aiResult.pension.income_start_age !== null) {
              const pensionStartAge = typeof aiResult.pension.income_start_age === 'number'
                ? aiResult.pension.income_start_age
                : parseInt(aiResult.pension.income_start_age);
              if (!isNaN(pensionStartAge) && pensionStartAge > 0 && pensionStartAge <= 100) {
                onboardingProgress["pension_income_start_age"] = pensionStartAge.toString();
              }
            }

            // Set pension monthly income if provided
            if (aiResult.pension.monthly_income !== undefined && aiResult.pension.monthly_income !== null) {
              const pensionMonthlyIncome = typeof aiResult.pension.monthly_income === 'number'
                ? aiResult.pension.monthly_income
                : parseNumericValue(aiResult.pension.monthly_income) || 0;
              onboardingProgress["pension_monthly_income"] = Math.max(0, pensionMonthlyIncome).toString();
            }
          } else {
            onboardingProgress["pension_plan"] = "No";
          }
        }
      }

      // Annuity - Extract annuity plan information
      if (aiResult.annuity && typeof aiResult.annuity === 'object') {
        // Set annuity plan status
        if (aiResult.annuity.plan !== undefined && aiResult.annuity.plan !== null) {
          const annuityPlan = String(aiResult.annuity.plan).trim();
          if (annuityPlan.toLowerCase() === 'yes' || annuityPlan.toLowerCase() === 'y') {
            onboardingProgress["annuity_plan"] = "Yes";

            // Set annuity start age if provided
            if (aiResult.annuity.income_start_age !== undefined && aiResult.annuity.income_start_age !== null) {
              const annuityStartAge = typeof aiResult.annuity.income_start_age === 'number'
                ? aiResult.annuity.income_start_age
                : parseInt(aiResult.annuity.income_start_age);
              if (!isNaN(annuityStartAge) && annuityStartAge > 0 && annuityStartAge <= 100) {
                onboardingProgress["annuity_income_start_age"] = annuityStartAge.toString();
              }
            }

            // Set annuity monthly income if provided
            if (aiResult.annuity.monthly_income !== undefined && aiResult.annuity.monthly_income !== null) {
              const annuityMonthlyIncome = typeof aiResult.annuity.monthly_income === 'number'
                ? aiResult.annuity.monthly_income
                : parseNumericValue(aiResult.annuity.monthly_income) || 0;
              onboardingProgress["annuity_monthly_income"] = Math.max(0, annuityMonthlyIncome).toString();
            }
          } else {
            onboardingProgress["annuity_plan"] = "No";
          }
        }
      }

      // Housing, location (optional)
      const housingOptions = ['Rent', 'Own', 'In Transition'];
      if (aiResult.housing_situation != null) {
        const housing = String(aiResult.housing_situation).trim();
        if (housingOptions.includes(housing)) {
          onboardingProgress["housing_situation"] = housing;
        }
      }
      if (aiResult.state != null && String(aiResult.state).trim()) {
        const state = String(aiResult.state).trim();
        if (state.length <= 100) onboardingProgress["state"] = state;
      }
      if (aiResult.city != null && String(aiResult.city).trim()) {
        const city = String(aiResult.city).trim();
        if (city.length <= 100) onboardingProgress["city"] = city;
      }

      // CRITICAL: Final validation and normalization before calculations
      // Ensure family_situation is normalized (even if from baseline)
      // Also check children_number to ensure correct "with children" vs "no kids" status
      if (onboardingProgress["family_situation"]) {
        let normalized = normalizeFamilySituation(onboardingProgress["family_situation"]);
        if (normalized) {
          // Check children_number to ensure correct status
          const childrenNumber = onboardingProgress["children_number"]
            ? parseInt(onboardingProgress["children_number"])
            : 0;
          if (!isNaN(childrenNumber) && childrenNumber > 0) {
            const lower = normalized.toLowerCase();
            // If status doesn't include children but user has children, update it
            if (lower.includes('married') && !lower.includes('children') && !lower.includes('kids')) {
              normalized = "Married with children";
            } else if (lower.includes('single') && !lower.includes('children') && !lower.includes('kids')) {
              normalized = "Single with children";
            } else if (lower.includes('divorced') && !lower.includes('children') && !lower.includes('kids')) {
              normalized = "Divorced with children";
            }
          } else if (childrenNumber === 0 || isNaN(childrenNumber)) {
            // User has no children, ensure status reflects that
            const lower = normalized.toLowerCase();
            if (lower.includes('married') && lower.includes('children')) {
              normalized = "Married, no kids";
            } else if (lower.includes('single') && lower.includes('children')) {
              normalized = "Single, no kids";
            } else if (lower.includes('divorced') && lower.includes('children')) {
              normalized = "Divorced, no kids";
            }
          }
          onboardingProgress["family_situation"] = normalized;
        }
      }

      // Ensure children_college_years is properly formatted array (string|null entries)
      if (onboardingProgress["children_college_years"]) {
        const normalized = parseChildrenCollegeData(onboardingProgress["children_college_years"]);
        if (normalized.length > 0) {
          onboardingProgress["children_college_years"] = normalized;
        } else if (Array.isArray(onboardingProgress["children_college_years"]) && onboardingProgress["children_college_years"].length === 0) {
          // Empty array is valid, keep it
        } else {
          // Invalid format, clear it
          delete onboardingProgress["children_college_years"];
        }
      }

      // Ensure pension plan defaults to "No" if not set (for chart calculations)
      if (!onboardingProgress["pension_plan"]) {
        onboardingProgress["pension_plan"] = "No";
      }

      // Ensure annuity plan defaults to "No" if not set (for chart calculations)
      if (!onboardingProgress["annuity_plan"]) {
        onboardingProgress["annuity_plan"] = "No";
      }

      // Ensure social security age defaults if not set
      if (!onboardingProgress["your_social_security_benefits_age"]) {
        onboardingProgress["your_social_security_benefits_age"] = FINANCIAL_CONSTANTS.SOCIAL_SECURITY_BENEFIT_AGE.toString();
      }

      const result = {
        duration: getDurationValue(onboardingProgress),
        spouseAgeText: getSpouseAgeText(onboardingProgress),
        healthcareCostsText: getHealthcareCostsText(onboardingProgress),
        socialSecurityText: getSocialSecurityText(onboardingProgress),
        longevityText: getLongevityText(onboardingProgress),
        financialIndependenceText: getFinancialIndependenceText(onboardingProgress),
        onboardingProgress // Return the converted progress for direct access
      };

      return result;
    }
  } catch (error) {
    logger.warn('AI calculation failed, falling back to converted data');
  }

  // Fallback: convert chat data and use original functions
  let fallbackProgress = convertV2OnboardingAiChatToOnboardingProgress(chatData);

  // Normalize family_situation in fallback
  if (fallbackProgress["family_situation"]) {
    const normalized = normalizeFamilySituation(fallbackProgress["family_situation"]);
    if (normalized) {
      fallbackProgress["family_situation"] = normalized;
    }
  }

  // Normalize children_college_years in fallback
  if (fallbackProgress["children_college_years"]) {
    const normalized = parseChildrenCollegeData(fallbackProgress["children_college_years"]);
    if (normalized.length > 0) {
      fallbackProgress["children_college_years"] = normalized;
    } else {
      // Keep as empty array if already array, otherwise remove
      if (!Array.isArray(fallbackProgress["children_college_years"])) {
        delete fallbackProgress["children_college_years"];
      }
    }
  }

  // Ensure defaults for fallback path (same as main path)
  if (!fallbackProgress["pension_plan"]) {
    fallbackProgress["pension_plan"] = "No";
  }
  if (!fallbackProgress["annuity_plan"]) {
    fallbackProgress["annuity_plan"] = "No";
  }
  if (!fallbackProgress["your_social_security_benefits_age"]) {
    fallbackProgress["your_social_security_benefits_age"] = FINANCIAL_CONSTANTS.SOCIAL_SECURITY_BENEFIT_AGE.toString();
  }

  const result = {
    duration: getDurationValue(fallbackProgress) || "1 year",
    spouseAgeText: getSpouseAgeText(fallbackProgress) || "While your spouse's/partner's age is not specified",
    healthcareCostsText: getHealthcareCostsText(fallbackProgress) || "Your healthcare costs approximately will be $197,500 lifetime for you.",
    socialSecurityText: getSocialSecurityText(fallbackProgress) || `Social Security benefits are assumed to start for you at age ${FINANCIAL_CONSTANTS.SOCIAL_SECURITY_BENEFIT_AGE}.`,
    longevityText: getLongevityText(fallbackProgress) || `For planning purposes, this scenario assumes you live up to ${LIFE_EXPECTANCY} years.`,
    financialIndependenceText: getFinancialIndependenceText(fallbackProgress) || "The goal is to achieve financial independence and retire.",
    onboardingProgress: fallbackProgress
  };

  return result;
};

/**
 * Maps simulation API charts to the shape expected by FinancialDashboard for Projected Net Worth.
 * API charts.* shape (ONBOARDING_AI_SIMULATION):
 *   assets[]:     { age, pre_tax, roth, brokerage, education_savings, equity_compensation, emergency_fund, total_investments_start, real_estate }
 *   liabilities[]: { age, mortgage, student_loan, car_loan, credit_card, other }
 *   net_worth[]:  { age, total_nominal, total_real }
 * All keys match SimulationOutput.charts from simulation_service_v3.py.
 * @param {Object} charts - charts from SimulationOutput (income, expenses, assets, liabilities, net_worth)
 * @param {number} retirementAge - retirement age
 * @param {number} endAge - end age (e.g. 95)
 * @returns {{ projectionData: Array, retirement: number, longevity: number }}
 */
const simulationChartsToProjectedNetWorth = (charts, retirementAge, endAge) => {
  if (!charts?.assets?.length || !charts?.liabilities?.length || !charts?.net_worth?.length) {
    return { projectionData: [], retirement: 0, longevity: 0 };
  }
  const n = charts.assets.length;
  const projectionData = [];
  let retirement = 0;
  let longevity = 0;
  for (let i = 0; i < n; i++) {
    const a = charts.assets[i] || {};
    const l = charts.liabilities[i] || {};
    const nw = charts.net_worth[i] || {};
    const age = Number(a.age) ?? 0;
    const realEstate = Math.round(Number(a.real_estate) || 0);
    const otherAssets = Math.round(Number(a.total_investments_start) || 0);
    const liabilities = Math.round(
      (Number(l.mortgage) || 0) + (Number(l.student_loan) || 0) + (Number(l.car_loan) || 0) +
      (Number(l.credit_card) || 0) + (Number(l.other) || 0)
    );
    const totalReal = Math.round(Number(nw.total_real) || 0);
    const totalNominal = Math.round(Number(nw.total_nominal) || 0);
    projectionData.push({
      age,
      realEstate,
      otherAssets,
      liabilities,
      currentDollar: totalReal,
      futureDollar: totalNominal
    });
    if (age === retirementAge) retirement = totalReal;
    if (age === endAge) longevity = totalReal;
  }
  return { projectionData, retirement, longevity };
};

/**
 * Maps simulation API charts to the shape expected by FinancialDashboard for cash flow (income + expenses).
 * Baseline charts (ONBOARDING_AI_SIMULATION `charts`): age-based arrays of objects; amounts are inflation-adjusted (real dollars).
 *   charts.income[]: flows including `education_savings_withdrawal` (529/education withdrawals that year) and `drawdown` (other portfolio draw).
 *   charts.assets[]: stocks including `education_savings` (remaining 529 balance that year)—do not merge into the income withdrawal series.
 * Monte Carlo `mc_failure_trace` / ft_* is a separate diagnostic (object-of-arrays, nominal $); not used here.
 * Work Income = wages + spouse_wages + career_shift.
 * @param {Object} charts - charts from SimulationOutput (simulation_service_v3)
 * @returns {{ data: Array<{ age, income, workIncome, socialSecurity, pension, annuity, rmd, collegeSavingsDrawdown, savingsDrawdown, educationSavingsWithdrawal, expenses }> }}
 */
const simulationChartsToProjectedCashFlow = (charts) => {
  if (!charts?.income?.length || !charts?.expenses?.length) {
    return { data: [] };
  }
  const income = charts.income;
  const expenses = charts.expenses;
  const data = income.map((inc, i) => {
    const exp = expenses[i] || {};
    const age = Number(inc.age) || 0;
    const wages = Number(inc.wages) || 0;
    const spouseWages = Number(inc.spouse_wages) || 0;
    const careerShift = Number(inc.career_shift) || 0;
    const workIncome = Math.round(wages + spouseWages + careerShift);
    const socialSecurity = Math.round((Number(inc.social_security) || 0) + (Number(inc.spouse_social_security) || 0));
    const educationSavingsWithdrawal = Math.round(Math.max(0, Number(inc.education_savings_withdrawal) || 0));
    const drawdown = Math.max(0, Number(inc.drawdown) || 0);
    const collegeSavingsDrawdown = educationSavingsWithdrawal;
    const savingsDrawdown = Math.round(drawdown);
    const expenseTotal = typeof exp.total_expenses === 'number' && !Number.isNaN(exp.total_expenses)
      ? exp.total_expenses
      : (Number(exp.general_expenses) || 0) + (Number(exp.college_expenses) || 0) + (Number(exp.debt_payments) || 0) + (Number(exp.taxes) || 0);
    return {
      age,
      income: workIncome,
      workIncome,
      socialSecurity,
      pension: Math.round(Number(inc.pension) || 0),
      annuity: Math.round(Number(inc.annuity) || 0),
      rmd: Math.round(Number(inc.rmd) || 0),
      collegeSavingsDrawdown,
      savingsDrawdown,
      educationSavingsWithdrawal,
      expenses: Math.round(expenseTotal)
    };
  });
  return { data };
};

const EMPTY_DASHBOARD = () => ({
  currentNetWorth: { value: 0, change: 0, changePercentage: 0, trend: [] },
  projectedNetWorth: { projectionData: [], retirement: 0, longevity: 0 },
  projectedCashFlow: { data: [] },
  retirementSuccess: { percentage: 0, description: 'Unable to load. Please log in and try again.' },
  liquidNetWorthAtRetirement: 0,
  liquidNetWorthAtDeath: 0,
  baselineFailureAge: null
});

/** SimulationInput defaults: same key names as backend simulation_service_v3.SimulationInput; values from FINANCIAL_CONSTANTS. */
const SIMULATION_DEFAULTS = {
  tuition_per_child_per_year: () => FINANCIAL_CONSTANTS.TUITION_PER_CHILD_PER_YEAR,
  inflation_rate: () => FINANCIAL_CONSTANTS.INFLATION_RATE,
  investment_mean_return: () => FINANCIAL_CONSTANTS.INVESTMENT_RETURN_RATE,
  investment_volatility: () => FINANCIAL_CONSTANTS.MONTE_CARLO_VOLATILITY,
  home_appreciation_rate: () => FINANCIAL_CONSTANTS.DEFAULT_HOME_APPRECIATION_RATE,
  mortgage_interest_rate: () => FINANCIAL_CONSTANTS.DEFAULT_MORTGAGE_INTEREST_RATE,
  mortgage_years_remaining: () => FINANCIAL_CONSTANTS.DEFAULT_MORTGAGE_YEARS_REMAINING,
  student_loan_interest_rate: () => FINANCIAL_CONSTANTS.DEFAULT_STUDENT_LOAN_INTEREST_RATE,
  student_loan_years_remaining: () => FINANCIAL_CONSTANTS.DEFAULT_STUDENT_LOAN_YEARS_REMAINING,
  car_loan_interest_rate: () => FINANCIAL_CONSTANTS.DEFAULT_CAR_LOAN_INTEREST_RATE,
  car_loan_years_remaining: () => FINANCIAL_CONSTANTS.DEFAULT_CAR_LOAN_YEARS_REMAINING,
  credit_card_interest_rate: () => FINANCIAL_CONSTANTS.DEFAULT_CREDIT_CARD_INTEREST_RATE,
  credit_card_payment_years: () => FINANCIAL_CONSTANTS.DEFAULT_CREDIT_CARD_PAYMENT_YEARS,
  credit_card_payment_mode: () => FINANCIAL_CONSTANTS.CREDIT_CARD_PAYMENT_MODE,
  other_debt_interest_rate: () => FINANCIAL_CONSTANTS.DEFAULT_OTHER_DEBT_INTEREST_RATE,
  other_debt_years_remaining: () => FINANCIAL_CONSTANTS.DEFAULT_OTHER_DEBT_YEARS_REMAINING,
  effective_tax_rate: () => FINANCIAL_CONSTANTS.EFFECTIVE_TAX_RATE,
  ss_taxable_portion: () => FINANCIAL_CONSTANTS.SS_TAXABLE_PORTION,
  return_analysis_details: () => FINANCIAL_CONSTANTS.DEFAULT_RETURN_ANALYSIS_DETAILS,
  return_mc_failure_trace: () => FINANCIAL_CONSTANTS.DEFAULT_RETURN_MC_FAILURE_TRACE,
  expenses_include_debts: () => FINANCIAL_CONSTANTS.DEFAULT_EXPENSES_INCLUDE_DEBTS,
  emergency_fund_amount: () => FINANCIAL_CONSTANTS.DEFAULT_EMERGENCY_FUND_AMOUNT,
  emergency_fund_balance: () => FINANCIAL_CONSTANTS.DEFAULT_EMERGENCY_FUND_BALANCE,
  emergency_fund_drawdown: () => FINANCIAL_CONSTANTS.DEFAULT_EMERGENCY_FUND_DRAWDOWN
};

/**
 * Builds the request body for ONBOARDING_AI_SIMULATION from onboarding progress.
 * @param {Object} onboardingProgress - Onboarding progress data
 * @returns {Object|null} Request body (no token) or null if current age invalid
 */
/**
 * Builds user_dob (ISO YYYY-MM-DD) from onboarding birth year/date for SimulationInput.
 * Backend uses this to compute current_age when provided.
 * @param {string} rawBirthValue - your_year_of_birth (year "YYYY" or full date)
 * @returns {string|null} ISO date or null
 */
const buildUserDobFromOnboarding = (rawBirthValue) => {
  if (!rawBirthValue) return null;
  const s = String(rawBirthValue).trim();
  const iso = parseDateToYYYYMMDD(s, false);
  if (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const y = parseYearFromText(s);
  return y ? `${y}-01-01` : null;
};

const buildSimulationRequestBody = (onboardingProgress) => {
  const currentAge = calculateAgeFromBirthYear(onboardingProgress["your_year_of_birth"]);
  if (currentAge == null || currentAge < 0) return null;

  const retirementAge = parseInt(onboardingProgress["retirement_age"]);
  const endAge = FINANCIAL_CONSTANTS.LIFE_EXPECTANCY;
  const ssStartAge = parseInt(onboardingProgress["your_social_security_benefits_age"], 10) || FINANCIAL_CONSTANTS.SOCIAL_SECURITY_BENEFIT_AGE;
  const { assets, debts } = extractAssetsAndDebts(onboardingProgress);

  const durationMonths = parseDurationToMonths(onboardingProgress);
  const monthlyNetDuringShift = Math.max(0, parseNumericValue(onboardingProgress["monthly_net_income_during_career_shift"]) || 0);
  const netAfterShift = Math.max(0, parseNumericValue(onboardingProgress["monthly_net_income_after_career_shift"]) || 0);
  const effectiveAfterShift = netAfterShift > 0 ? netAfterShift : monthlyNetDuringShift;
  const { baseAnnualIncome } = getBaseIncomeForYear(0, durationMonths, monthlyNetDuringShift * 12, effectiveAfterShift * 12);
  const monthlyIncomeEmployed = Math.max(0, Math.round((baseAnnualIncome / 12) * 100) / 100);

  const pensionPlan = String(onboardingProgress["pension_plan"] || '').toLowerCase();
  const annuityPlan = String(onboardingProgress["annuity_plan"] || '').toLowerCase();

  const user_dob = buildUserDobFromOnboarding(onboardingProgress["your_year_of_birth"]);
  const career_shift_start_date = durationMonths > 0
    ? new Date().toISOString().slice(0, 10)
    : null;

  const married = isMarried(onboardingProgress["family_situation"]);
  const spouseCurrentAge = married ? calculateAgeFromBirthYear(onboardingProgress["partner_year_of_birth_of_birth"]) : null;
  const spouseSSStartAge = married ? (parseInt(onboardingProgress["partner_social_security_benefits_age"], 10) || null) : null;
  const monthlySpouseSS = married && spouseSSStartAge ? (getSocialSecurityMonthlyBenefit(spouseSSStartAge) || 0) : 0;

  const annualRetirementContribution = Math.max(0, parseNumericValue(onboardingProgress["total_retirement_account_each_year"]) || 0);
  const monthlyRetirementContribution = annualRetirementContribution / 12;

  const defaults = Object.fromEntries(
    Object.entries(SIMULATION_DEFAULTS).map(([k, fn]) => [k, fn()])
  );

  return {
    run_monte_carlo: true,
    current_age: currentAge,
    retirement_age: retirementAge,
    end_age: endAge,
    user_dob: user_dob,
    pre_tax_balance: Math.max(0, parseNumericValue(assets.retirementAccounts) || 0),
    roth_balance: Math.max(0, parseNumericValue(assets.totalBalancePostTaxRetirementAccounts) || 0),
    brokerage_balance: Math.max(0, parseNumericValue(assets.otherInvestments) || 0),
    education_savings_balance: Math.max(0, parseNumericValue(assets.childrenCollegeSavings) || 0),
    equity_compensation_value: Math.max(0, parseNumericValue(assets.totalBalanceEquityCompensationAccounts) || 0),
    other_assets_value: Math.max(0, parseNumericValue(assets.currentMarketValue) || 0),
    home_value: Math.max(0, parseNumericValue(assets.homeEstimatedValue) || 0),
    monthly_retirement_contribution: monthlyRetirementContribution,
    monthly_savings_contribution: 0,
    mortgage_debt: Math.max(0, parseNumericValue(debts.homeMortgageDebt) || 0),
    student_loan_debt: Math.max(0, parseNumericValue(debts.educationalLoan) || 0),
    car_loan_debt: Math.max(0, parseNumericValue(debts.carLoanDebt) || 0),
    credit_card_debt: Math.max(0, parseNumericValue(debts.oweCreditCards) || 0),
    other_debt: Math.max(0, parseNumericValue(debts.otherDebtsLoans) || 0),
    monthly_income_employed: monthlyIncomeEmployed,
    salary_growth_rate: null,
    monthly_ss_income: getSocialSecurityMonthlyBenefit(ssStartAge) || 0,
    ss_start_age: ssStartAge,
    spouse_work_income: married ? Math.max(0, parseNumericValue(onboardingProgress["monthly_net_income_partner"]) || 0) : 0,
    spouse_retirement_age: married ? (parseInt(onboardingProgress["partner_retire"], 10) || null) : null,
    monthly_spouse_ss_income: monthlySpouseSS,
    spouse_ss_start_age: spouseSSStartAge,
    spouse_current_age: spouseCurrentAge,
    monthly_pension_income: pensionPlan.includes('yes') ? Math.max(0, parseNumericValue(onboardingProgress["pension_monthly_income"]) || 0) : 0,
    pension_start_age: pensionPlan.includes('yes') ? (parseInt(onboardingProgress["pension_income_start_age"], 10) || null) : null,
    monthly_annuity_income: annuityPlan.includes('yes') ? Math.max(0, parseNumericValue(onboardingProgress["annuity_monthly_income"]) || 0) : 0,
    annuity_start_age: annuityPlan.includes('yes') ? (parseInt(onboardingProgress["annuity_income_start_age"], 10) || null) : null,
    career_shift_choice: FINANCIAL_CONSTANTS.DEFAULT_CAREER_SHIFT_CHOICE,
    career_shift_months: Math.round(durationMonths),
    career_shift_start_date: career_shift_start_date,
    career_shift_income: Math.max(0, monthlyNetDuringShift),
    monthly_income_after_shift: Math.max(0, effectiveAfterShift),
    monthly_expenses: Math.max(0, parseNumericValue(onboardingProgress["monthly_expenses"]) || 0),
    child_college_start_years: extractNumericCollegeYears(onboardingProgress["children_college_years"]) || [],
    current_year: new Date().getFullYear(),
    ...defaults
  };
};

/**
 * Fetches financial dashboard data from ONBOARDING_AI_SIMULATION and maps response to dashboard shape.
 * @param {Object} onboardingProgress - Onboarding progress data
 * @param {AbortSignal|null} [signal=null] - Optional abort signal to cancel the request (e.g. when effect re-runs in React Strict Mode)
 * @returns {Promise<Object>} { currentNetWorth, projectedNetWorth, projectedCashFlow, retirementSuccess, liquidNetWorthAtRetirement, liquidNetWorthAtDeath }
 */
export const getFinancialDashboardData = async (onboardingProgress, signal = null) => {
  const empty = EMPTY_DASHBOARD;
  try {
    const token = getToken();
    if (!token) {
      logger.error('No authentication token for Monte Carlo simulation');
      return empty();
    }
    const requestBody = buildSimulationRequestBody(onboardingProgress);
    if (!requestBody) {
      logger.error('Unable to calculate current age for Monte Carlo simulation');
      return empty();
    }

    const response = await fetch(`${config.API_BASE_URL}${config.API_ENDPOINTS.ONBOARDING_AI_SIMULATION}`, {
      method: 'POST',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ ...requestBody, token }),
      ...(signal && { signal })
    });

    if (!response.ok) {
      logger.error(`Monte Carlo simulation API error: ${response.status} - ${await response.text().catch(() => '')}`);
      return empty();
    }

    const data = await response.json();
    if (!data || typeof data !== 'object') {
      logger.error('Invalid response from Monte Carlo simulation API', data);
      return empty();
    }

    const successRate = readLongevitySuccessScore(data);
    const hasValidScore = successRate != null;
    const description = !hasValidScore
      ? RETIREMENT_SUCCESS_UNABLE_TO_CALCULATE
      : successRate >= 80
        ? "Congrats! 🎉 You have a very high chance of funding your plan through life. We want everyone to reach this level of financial freedom — it's the benchmark our CFP team helps clients achieve."
        : successRate >= 40
          ? "You're getting close! 💪 Your plan's on the right track and nearing the CFP benchmark for long-term success. A few small tweaks could boost your confidence and help you reach that next level of financial freedom."
          : RETIREMENT_SUCCESS_ROOM_TO_GROW;
    const retirementSuccess = {
      percentage: hasValidScore ? successRate : null,
      description
    };

    if (!data.charts) {
      logger.warn('Simulation API response missing charts');
      return { ...empty(), retirementSuccess };
    }

    const retirementAge = requestBody.retirement_age;
    const endAge = requestBody.end_age;
    const projectedNetWorth = simulationChartsToProjectedNetWorth(data.charts, retirementAge, endAge);
    const projectedCashFlow = simulationChartsToProjectedCashFlow(data.charts);
    const baselineFailureAge = typeof data.baseline_failure_age === 'number' && !Number.isNaN(data.baseline_failure_age)
      ? data.baseline_failure_age
      : null;

    const firstNw = data.charts.net_worth?.[0];
    const currentValue = firstNw && typeof firstNw.total_real === 'number' ? Math.round(firstNw.total_real) : 0;
    const { result: monthlyChange } = calculateExpenseResult(onboardingProgress);
    const changePct = currentValue > 0 ? (monthlyChange / currentValue) * 100 : 0;

    return {
      baselineFailureAge,
      currentNetWorth: {
        value: currentValue,
        change: monthlyChange,
        changePercentage: changePct,
        trend: generateTrendData(currentValue - monthlyChange, monthlyChange)
      },
      projectedNetWorth,
      projectedCashFlow,
      retirementSuccess,
      liquidNetWorthAtRetirement: projectedNetWorth.retirement || 0,
      liquidNetWorthAtDeath: projectedNetWorth.longevity || 0
    };
  } catch (error) {
    if (error?.name === 'AbortError' || signal?.aborted) throw error;
    logger.error('Error in getFinancialDashboardData:', error);
    return EMPTY_DASHBOARD();
  }
};

/**
 * Fetches raw simulation output for use with Chart Generator API.
 * Same flow as getFinancialDashboardData but returns raw API response and request body.
 * @param {Object} onboardingProgress - Onboarding progress data
 * @param {AbortSignal|null} [signal=null] - Optional abort signal
 * @returns {Promise<{output: object|null, requestBody: object|null}>}
 */
export const fetchSimulationOutputForCharts = async (onboardingProgress, signal = null) => {
  try {
    const token = getToken();
    if (!token) {
      logger.error('No authentication token for simulation');
      return { output: null, requestBody: null };
    }
    const requestBody = buildSimulationRequestBody(onboardingProgress);
    if (!requestBody) {
      logger.error('Unable to build simulation request body (invalid current age)');
      return { output: null, requestBody: null };
    }

    const response = await fetch(`${config.API_BASE_URL}${config.API_ENDPOINTS.ONBOARDING_AI_SIMULATION}`, {
      method: 'POST',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ ...requestBody, token }),
      ...(signal && { signal })
    });

    if (!response.ok) {
      logger.error(`Simulation API error ${response.status}: ${await response.text().catch(() => '')}`);
      return { output: null, requestBody };
    }

    const data = await response.json();
    if (!data || typeof data !== 'object') {
      logger.error('Invalid simulation API response', data);
      return { output: null, requestBody };
    }

    return { output: data, requestBody };
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    logger.error('Error in fetchSimulationOutputForCharts:', error);
    return { output: null, requestBody: null };
  }
};
